/**
 * Report Narrative Hygiene (RNH pack) — post-generation cleanup of
 * verifier-tag markers and stance-aware action softening.
 *
 * The narrative prompt instructs the LLM to emit
 *   `[verified sed-section:<id>]`
 * inline whenever it grounds a claim in SED evidence. The post-gen
 * claim extraction (`extractReportClaims` / `applyClaimsEmission` in
 * src/services/claimExtraction.ts) reads these markers to populate
 * `report.claims[].sources` with `sed-section:<id>` prefixes.
 *
 * Once CSE has read the markers they have no further job — leaving
 * them in the persisted narrative makes the report read like raw
 * tooling output to the customer:
 *
 *   "Substation Z is 1.2 mi from the parcel [verified sed-section:grid]."
 *
 * `stripVerifierTags` removes those markers from every narrative
 * surface AFTER CSE has had its turn. Punctuation adjacency is
 * preserved (the regex absorbs the leading space when present so the
 * resulting prose collapses cleanly), and the bare
 * `[verified sed-section]` form with no id is intentionally left
 * untouched — that shape can only land via a malformed prompt and
 * deserves human review, not silent erasure.
 *
 * Pure / side-effect free apart from a single `console.info`
 * breadcrumb when at least one tag was stripped — this is gen-time,
 * NOT a render/effect path, so the wave-3 + wave-5 `!cancelled`
 * gating rule for async effects does not apply (no async effect, no
 * cancellation token to gate on).
 *
 * Lazy render-time strip for legacy reports lives next to the
 * renderer (`SiteRagReportView`) so the persisted body stays untouched
 * until a one-shot backfill (RNH-FOLLOWUP-BACKFILL-STRIP) rewrites
 * the archived corpus.
 */
import type { SiteRagReport } from '../types/siteRag';
import {
  formatReportLeafPath,
  parseReportLeafPath,
  reportLeafLabel,
  WITHHELD_NARRATIVE_NOTICE,
  WITHHELD_NARRATIVE_SPAN_MARKER,
  type ReportLeafPathSegment,
  type WithheldNarrativeLeaf,
} from '../lib/report/withheldNarrative';
import { findManifestDrift } from '../lib/coverage/evidenceConsolidation';
import {
  recommendAlternativeOptions as recommendAlternativeOptionsImpl,
  type AltOptionFinding,
  type AltOptionSiteProfile,
} from './sentiment/altOptionRecommender';

/**
 * Monotonic version pin for the SET of hygiene rules exported by this
 * module. NB-3 — bump this whenever any strip / detect / flag rule in
 * this file changes shape (regex tweak, new family, new exemption,
 * etc.). The render-side lazy backfill (`applyHygieneBackfill` in
 * `narrativeBackfill.ts`, mounted via `SiteRagReportView`'s memo key)
 * INCLUDES this constant in its memo key, so:
 *
 *   1. A bump invalidates every render-side strip memo on next read.
 *   2. Archived reports that were previously memoised under the old
 *      version get re-stripped lazily.
 *   3. The persisted body is NEVER rewritten — reproducibility is
 *      preserved, only the in-memory cleaned copy refreshes.
 *
 * String — keep it monotonic ('1' → '2' → '3' …). Numeric type works
 * too but a string is friendlier when this surfaces in a chip tooltip
 * (NB-4 admin chip).
 *
 * Initial value: '1' (matches the rule set committed when NB-1..4
 * shipped: RNH-5 verifier tags + MEL meta-instructions + KFH-3
 * placeholders + WPG-2 workload drift + WPG-3 capacity drift).
 */
export const HYGIENE_VERSION = '4';

/**
 * Match the verifier-tag marker that the LLM emits inline.
 *
 *   `[verified sed-section:<id>]` — optionally preceded by exactly
 *   one space. The leading-space match lets the strip collapse
 *   `grounded [verified sed-section:grid]` to `grounded` (no
 *   trailing space) without leaving a double-space when the tag was
 *   mid-sentence.
 *
 * The `<id>` allows hyphens, DOTS, and word characters; the bare
 * `[verified sed-section]` form (no `:id`) is intentionally NOT
 * matched (see file-level docstring). The dot is required because the
 * generator sometimes emits a dotted path (e.g. `water.source`) rather
 * than a bare id — golden-pass 2026-06-27 found
 * `[verified sed-section:water.source]` LEAKING into published prose
 * because the old `[\w-]+` class stopped at the dot, leaving the marker
 * un-stripped.
 *
 * Exported so the lazy render-time strip path (SiteRagReportView)
 * and tests can share the exact same regex.
 */
export const VERIFIER_TAG_REGEX = /\s?\[verified\s+sed-section:[\w.-]+\]/gi;

/**
 * Punctuation-tightening pass. After the marker is removed, fix up
 * the two cosmetic regressions that can result:
 *   - `"foo  bar"`   (double space)         → `"foo bar"`
 *   - `"foo .bar"`   (space before .,;:)    → `"foo.bar"`
 *
 * Tested directly so adjacent inline citations like `[Ref 3]` survive
 * untouched.
 */
function tightenPunctuation(s: string): string {
  return s.replace(/ {2,}/g, ' ').replace(/ +([.,;:])/g, '$1');
}

/**
 * Strip a single narrative string. Returns `{ next, count }` where
 * `count` is the number of tags removed. When no tags were present
 * the original string is returned by reference identity so callers
 * can no-op cheaply.
 */
export function stripVerifierTagsFromText(body: string): { next: string; count: number } {
  if (!body) return { next: body, count: 0 };
  let count = 0;
  // Spread the regex into a fresh instance for each call so the
  // global-flag `lastIndex` state doesn't leak between strings.
  const re = new RegExp(VERIFIER_TAG_REGEX.source, VERIFIER_TAG_REGEX.flags);
  const replaced = body.replace(re, () => {
    count++;
    return '';
  });
  if (count === 0) return { next: body, count: 0 };
  return { next: tightenPunctuation(replaced), count };
}

/**
 * Names of the top-level string fields on SiteRagReport that carry
 * LLM-generated prose. Kept in sync with the parser's `collectNarrativeSections`
 * (src/services/claimExtraction.ts) so a section the LLM emitted a
 * marker into is one we strip on the way out.
 *
 * `summary` is the cover-page line — short, but the LLM has been
 * observed to leak the tag there too (DOM-confirmed Sickel v1.0).
 */
const TOP_LEVEL_STRING_FIELDS = [
  'executiveSummary',
  'summary',
  'sitingNotes',
  'projectIntentOverview',
] as const;

/**
 * Names of the typed-object subtrees on SiteRagReport whose leaf
 * string properties can carry verifier markers. Mirrors the parser's
 * walk list. Only string + string[] leaves are visited; objects/
 * arrays-of-objects recurse one more level for two-deep structures
 * (`btmDetailedAnalysis[]`, etc.).
 */
const NARRATIVE_OBJECT_FIELDS = [
  'climateSevereWeather',
  'infrastructureInterconnection',
  'gridStudy',
  'transportStudy',
  'connectivityStudy',
  'nepaScreening',
  'ecologicalAssessment',
  'waterResources',
  'airQualityEmissions',
  'floodNaturalHazards',
  'culturalHistorical',
  'landUseZoning',
  'noiseVisualImpact',
  'mitigationPlan',
  'permittingPathway',
  'environmentalRiskMatrix',
  'powerAnalysis',
  'btmDetailedAnalysis',
  'gridVsBtmComparison',
  'lcoeDetailedAnalysis',
  'waterAnalysis',
  'testFitAnalysis',
  'interconnectionAnalysis',
  'conclusion',
] as const;

/**
 * Walk a value and strip verifier tags from every string leaf.
 *
 * - string                → strip
 * - string[]              → map(strip)
 * - object (Record)       → recurse one level (typed-object subtree)
 * - array of object       → map(recurse one level)
 * - anything else (number, boolean, null, undefined, Date) → identity
 *
 * Returns `{ next, count }`. When `count === 0` the original input is
 * returned by reference identity, so callers can skip an
 * object-spread on the way out.
 */
function stripFromValue(value: unknown, depth = 0): { next: unknown; count: number } {
  if (value === null || value === undefined) return { next: value, count: 0 };
  if (typeof value === 'string') return stripVerifierTagsFromText(value);
  if (Array.isArray(value)) {
    let total = 0;
    let mutated = false;
    const nextArr = value.map(v => {
      const r = stripFromValue(v, depth + 1);
      if (r.count > 0) {
        total += r.count;
        mutated = true;
      }
      return r.next;
    });
    return mutated ? { next: nextArr, count: total } : { next: value, count: 0 };
  }
  if (typeof value === 'object') {
    // Depth-limit: we only descend into typed narrative subtrees one
    // step beyond the entry point. Two levels covers shapes like
    // `btmDetailedAnalysis: [{ technology: '…', capitalCost: '…' }]`
    // (array-of-object, one strip level inside the object).
    if (depth >= 3) return { next: value, count: 0 };
    let total = 0;
    let mutated = false;
    const nextObj: Record<string, unknown> = { ...(value as Record<string, unknown>) };
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const r = stripFromValue(v, depth + 1);
      if (r.count > 0) {
        total += r.count;
        mutated = true;
        nextObj[k] = r.next;
      }
    }
    return mutated ? { next: nextObj, count: total } : { next: value, count: 0 };
  }
  return { next: value, count: 0 };
}

/**
 * Strip verifier tags from every narrative surface on the report.
 *
 * MUST be called AFTER `applyClaimsEmission` has read the markers and
 * populated `report.claims[].sources` — the regex-driven CSE harvest
 * depends on the literal text being present in the body.
 *
 * Order of operations in the gen pipeline:
 *   1. generateNarrative()        — emits `[verified sed-section:X]` inline.
 *   2. applyClaimsEmission()      — harvests markers into `claims[].sources`.
 *   3. stripVerifierTags(report)  — removes markers from persisted prose.
 *   4. persistReport(stripped)    — Firestore write.
 *
 * Pure: returns a NEW report object when at least one string was
 * modified, OR the original input by reference identity when no tags
 * were found anywhere. The shallow clone shares unmodified subtrees
 * with the input so downstream identity checks (e.g. memoised
 * selectors) still hit on the un-touched branches.
 */
export function stripVerifierTags(report: SiteRagReport): SiteRagReport {
  if (!report) return report;
  let total = 0;
  const patch: Record<string, unknown> = {};

  for (const key of TOP_LEVEL_STRING_FIELDS) {
    const v = (report as unknown as Record<string, unknown>)[key];
    if (typeof v !== 'string') continue;
    const r = stripVerifierTagsFromText(v);
    if (r.count > 0) {
      total += r.count;
      patch[key] = r.next;
    }
  }

  for (const key of NARRATIVE_OBJECT_FIELDS) {
    const v = (report as unknown as Record<string, unknown>)[key];
    const r = stripFromValue(v);
    if (r.count > 0) {
      total += r.count;
      patch[key] = r.next;
    }
  }

  if (total === 0) return report;
  console.info(`[stripVerifierTags] removed ${total} tags from ${report.id}`);
  return { ...report, ...patch } as SiteRagReport;
}

/* ─── EMD — em/en-dash elimination ──────────────────────────────────
 *
 * William 2026-06-29: the generated narrative must NOT contain
 * em-dashes or en-dashes (spaced OR standalone). The narrative prompt
 * (anti-AI-voice bundle in reportPrompts.ts) now forbids them outright,
 * but the LLM still emits them, so this post-gen strip is the
 * enforcement net. It is a sibling of `stripVerifierTags` — same walk
 * over the same narrative field lists (TOP_LEVEL_STRING_FIELDS +
 * NARRATIVE_OBJECT_FIELDS), same identity-on-clean short-circuit — and
 * is wired into the SAME gen-time + render-time call sites.
 *
 * Two passes per string:
 *   1. spaced em/en dash   " — " / " – "   →  ", "   (the form the user
 *      explicitly rejects; the comma keeps the clause break readable)
 *   2. remaining em/en dash "—" / "–"       →  " - "  (one-sided or
 *      standalone; downgraded to a plain spaced hyphen)
 *
 * Only U+2014 (em) and U+2013 (en) are targeted. The ASCII hyphen-minus
 * (U+002D) inside words / ranges ("AI-training", "10-50 MW") is NEVER
 * touched, and citation / markdown tokens ([Ref 3], **bold**, `- ` list
 * bullets) carry no em/en dashes so they survive untouched. Idempotent:
 * a cleaned body has no em/en dashes left, so a re-run is a no-op.
 */

/**
 * Strip em/en dashes from a single narrative string. Returns
 * `{ next, count }` where `count` is the number of dashes rewritten.
 * When none were present the original string is returned by reference
 * identity so callers can no-op cheaply. Reuses `tightenPunctuation`
 * to collapse the incidental double-space the standalone-dash rewrite
 * can introduce next to an existing space.
 */
export function stripEmDashesFromText(body: string): { next: string; count: number } {
  if (!body) return { next: body, count: 0 };
  let count = 0;
  // 1. Spaced em/en dash → ", ".
  let replaced = body.replace(/ [—–] /g, () => {
    count++;
    return ', ';
  });
  // 2. Any remaining (standalone / one-sided) em/en dash → " - ". A
  //    leading/trailing space the original already carried becomes a
  //    double space, collapsed by tightenPunctuation below.
  replaced = replaced.replace(/[—–]/g, () => {
    count++;
    return ' - ';
  });
  if (count === 0) return { next: body, count: 0 };
  return { next: tightenPunctuation(replaced), count };
}

/**
 * Walk a value and strip em/en dashes from every string leaf. Mirrors
 * `stripFromValue` (the verifier-tag walker) exactly — same depth bound
 * and same reference-identity-on-clean contract.
 */
function stripEmDashesFromValue(value: unknown, depth = 0): { next: unknown; count: number } {
  if (value === null || value === undefined) return { next: value, count: 0 };
  if (typeof value === 'string') return stripEmDashesFromText(value);
  if (Array.isArray(value)) {
    let total = 0;
    let mutated = false;
    const nextArr = value.map(v => {
      const r = stripEmDashesFromValue(v, depth + 1);
      if (r.count > 0) {
        total += r.count;
        mutated = true;
      }
      return r.next;
    });
    return mutated ? { next: nextArr, count: total } : { next: value, count: 0 };
  }
  if (typeof value === 'object') {
    if (depth >= 3) return { next: value, count: 0 };
    let total = 0;
    let mutated = false;
    const nextObj: Record<string, unknown> = { ...(value as Record<string, unknown>) };
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const r = stripEmDashesFromValue(v, depth + 1);
      if (r.count > 0) {
        total += r.count;
        mutated = true;
        nextObj[k] = r.next;
      }
    }
    return mutated ? { next: nextObj, count: total } : { next: value, count: 0 };
  }
  return { next: value, count: 0 };
}

/**
 * Strip em/en dashes from every narrative surface on the report.
 *
 * Sibling of `stripVerifierTags` — runs in the SAME gen-time pipeline
 * slot (right after the verifier-tag strip) and the SAME render-time
 * backfill, walking the SAME field lists. Pure: returns a NEW report
 * object when at least one string was modified, OR the original input
 * by reference identity when no em/en dashes were found anywhere.
 */
export function stripEmDashes(report: SiteRagReport): SiteRagReport {
  if (!report) return report;
  let total = 0;
  const patch: Record<string, unknown> = {};

  for (const key of TOP_LEVEL_STRING_FIELDS) {
    const v = (report as unknown as Record<string, unknown>)[key];
    if (typeof v !== 'string') continue;
    const r = stripEmDashesFromText(v);
    if (r.count > 0) {
      total += r.count;
      patch[key] = r.next;
    }
  }

  for (const key of NARRATIVE_OBJECT_FIELDS) {
    const v = (report as unknown as Record<string, unknown>)[key];
    const r = stripEmDashesFromValue(v);
    if (r.count > 0) {
      total += r.count;
      patch[key] = r.next;
    }
  }

  if (total === 0) return report;
  console.info(`[stripEmDashes] removed ${total} em/en dashes from ${report.id}`);
  return { ...report, ...patch } as SiteRagReport;
}

/* ─── RNH-2 — post-gen guard for assertive nuclear actions ───────── */

/**
 * Regex matching the assertive nuclear-action patterns that should
 * NOT appear when `projectIntent.nuclearInterest` is `'considering'`
 * (or `'undecided'` — same conservative lane).
 *
 * Triggers on EITHER of these alternatives:
 *   (A) "Immediately begin/file/submit/commit/commission" within the
 *       same sentence as a nuclear-domain token
 *       (ESP, SMR, nuclear, PSHA, NRC, COL). Sentence delimiter is
 *       approximated by "not sentence-ending punctuation".
 *   (B) "Commit to BTM SMR" — verbatim phrase live-prod produced;
 *       self-contained (the SMR token is inside the trigger phrase).
 *
 * Two separate alternatives so the `Commit to BTM SMR` form doesn't
 * have to find another nuclear token after it to match.
 *
 * `gi` flag — global so we count every occurrence; case-insensitive
 * so the LLM's casing variation ("immediately" vs "Immediately") is
 * caught.
 */
const ASSERTIVE_NUCLEAR_REGEX =
  /(?:\bImmediately\s+(?:begin|file|submit|commit|commission)\b[^.!?]*?\b(?:ESP|SMR|nuclear|PSHA|NRC|COL)\b)|(?:\bCommit\s+to\s+BTM\s+SMR\b)/gi;

/**
 * RNH-FOLLOWUP-CONDITIONAL-EXEMPT (2026-05-18).
 *
 * RNH-1's stance gating instructs the LLM to SOFTEN assertive nuclear
 * actions under the `'considering'` lane by wrapping them in
 * conditional clauses ("If pursuing the SMR pathway, …" /
 * "Should the SMR option be selected, …"). The post-gen RNH-2 guard
 * (ASSERTIVE_NUCLEAR_REGEX) catches the residual assertive verbs the
 * LLM may emit despite that instruction — but it does NOT distinguish
 * the BARE form ("Immediately commission a PSHA …") from the SOFTENED
 * form RNH-1 successfully produced ("If pursuing the SMR pathway,
 * immediately commission a PSHA …"). The latter IS the desired
 * output; flagging it as a stance violation produces false positives.
 *
 * Live-prod Sickel v2.0: 3 softened "If pursuing…" sentences in the
 * Action Plan, 1 false-positive `rnh-stance-mismatch` flag belonging
 * to one of those softened sentences.
 *
 * Mitigation: when a match's containing sentence STARTS with a
 * conditional preamble, treat the soft form as honored and skip the
 * flag.
 */
const CONDITIONAL_PREAMBLES: ReadonlyArray<RegExp> = [
  /\bIf\s+pursuing\b/i,
  /\bIf\s+the\s+project\s+(?:elects|chooses|opts)\s+to\b/i,
  /\bShould\s+the\s+(?:\w+\s+){0,4}(?:pathway|option|route)\s+be\s+selected\b/i,
  /\bIf\s+you\s+(?:decide|elect)\s+to\s+(?:proceed|advance)\s+with\b/i,
  /\bConditional\s+on\s+selecting\s+the\b/i,
  /\bUpon\s+committing\s+to\b/i,
  /\bShould\s+the\s+developer\s+(?:elect|choose|opt)\b/i,
  // PSE-3 — sentiment-aware conditionals that the PSE-1 prompt guidance
  // block instructs the LLM to use when recommending techs with
  // oppositional local sentiment. Mirrored here so the post-gen guard
  // recognises the softened forms and doesn't re-flag them.
  /\bSubject\s+to\s+(?:local\s+)?(?:community|stakeholder)\s+engagement\b/i,
  /\bSubject\s+to\s+community\s+(?:support|consultation|acceptance)\b/i,
  /\bIf\s+sentiment\s+(?:improves|remains|shifts)\b/i,
  /\bIf\s+local\s+sentiment\b/i,
  /\bContingent\s+on\s+(?:community|stakeholder)\b/i,
  /\bPending\s+(?:community|stakeholder)\s+engagement\b/i,
];

/**
 * Returns true when `matchIdx` (the start index of an
 * ASSERTIVE_NUCLEAR_REGEX hit inside `body`) lies in a sentence whose
 * left-edge contains one of `CONDITIONAL_PREAMBLES`.
 *
 * The lookback is intentionally bounded (120 chars) and clipped at the
 * last sentence-ending boundary, so a conditional preamble in a
 * DIFFERENT sentence cannot leak across to exempt this match. The
 * regression invariant the pack pins:
 *
 *   "If pursuing the SMR pathway, the team should review options.
 *    Immediately commission a PSHA …"
 *
 * Still flagged — the "Immediately commission a PSHA" is a fresh
 * sentence whose left edge is "Immediately", not "If pursuing".
 *
 * Pure / side-effect free.
 */
function isWrappedInConditional(body: string, matchIdx: number): boolean {
  const lookbackStart = Math.max(0, matchIdx - 120);
  const lookback = body.slice(lookbackStart, matchIdx);
  // Clip at the last sentence boundary (`. ` / `! ` / `? ` / `\n`) so
  // we only inspect the CURRENT sentence's left edge.
  let currentSentence = lookback;
  let lastBoundary = -1;
  for (let i = lookback.length - 2; i >= 0; i--) {
    const ch = lookback[i];
    if (ch === '\n') {
      lastBoundary = i + 1;
      break;
    }
    if ((ch === '.' || ch === '!' || ch === '?') && /\s/.test(lookback[i + 1] ?? '')) {
      lastBoundary = i + 1;
      break;
    }
  }
  if (lastBoundary >= 0) {
    // Skip the punctuation + whitespace to reach the next sentence's
    // first word.
    let j = lastBoundary;
    while (j < lookback.length && /[.!?\s]/.test(lookback[j] ?? '')) j++;
    currentSentence = lookback.slice(j);
  }
  return CONDITIONAL_PREAMBLES.some(p => p.test(currentSentence));
}

/**
 * Walk a narrative surface and return every match of the assertive-
 * nuclear pattern alongside the `sectionId` it came from. Pure.
 *
 * RNH-FOLLOWUP-CONDITIONAL-EXEMPT: matches whose containing sentence
 * begins with a conditional preamble (CONDITIONAL_PREAMBLES) are
 * skipped — that IS the softened form RNH-1's stance gating asked
 * for.
 */
function collectAssertiveMatches(
  sectionId: string,
  value: unknown,
  depth = 0,
  acc: Array<{ sectionId: string; phrase: string }> = [],
  // RNH-2b — lane-specific regex + exemption. Defaults to the
  // 'considering' guard so existing call sites are byte-identical.
  guard: StanceGuardConfig = CONSIDERING_GUARD,
): Array<{ sectionId: string; phrase: string }> {
  if (value === null || value === undefined) return acc;
  if (typeof value === 'string') {
    // Use .exec to get the match index — needed for the conditional-
    // preamble exemption check. Fresh regex per call.
    const re = new RegExp(guard.regex.source, guard.regex.flags);
    let m: RegExpExecArray | null;
    while ((m = re.exec(value)) !== null) {
      if (guard.exempt(value, m.index)) continue;
      acc.push({ sectionId, phrase: m[0] });
    }
    return acc;
  }
  if (Array.isArray(value)) {
    if (depth >= 3) return acc;
    for (const v of value) collectAssertiveMatches(sectionId, v, depth + 1, acc, guard);
    return acc;
  }
  if (typeof value === 'object') {
    if (depth >= 3) return acc;
    for (const v of Object.values(value as Record<string, unknown>)) {
      collectAssertiveMatches(sectionId, v, depth + 1, acc, guard);
    }
    return acc;
  }
  return acc;
}

/**
 * Inline softening prefix. When the post-gen guard finds an
 * assertive nuclear action under a `'considering'` stance, we prefix
 * the offending phrase with a beacon so the editor (and any
 * automated rerun) can spot it visually. The verbatim phrase is left
 * intact for traceability — the renderer surfaces the
 * `qualityWarnings` chip as the primary signal; this is a
 * belt-and-braces fallback for users who skim past the chip.
 *
 * Bracketed shape so it visually rhymes with the existing reliability
 * tags ([verified ...], [inferred ...]) the reader already knows.
 */
const STANCE_SOFTEN_PREFIX = '[stance: considering — soften before publish] ';

/* ─── RNH-2b — 'not-considered' lane (Pronghorn, 2026-08-17) ───────── */

/**
 * RNH-2b — nuclear RECOMMENDATION detector for the `'not-considered'`
 * lane. Under `nuclearInterest: 'no'` the gen-time stance block (RNH-1)
 * tells the model to suppress the nuclear pathway from RECOMMENDED
 * ACTIONS entirely — so post-gen, ANY recommendation-shaped sentence
 * that advances a nuclear pathway is a stance mismatch, including the
 * conditional forms that are legitimate under `'considering'`
 * ("If pursuing the SMR pathway, …" is still a pathway the user excluded).
 *
 * Live-prod trigger: Pronghorn financial report recommended a "phased
 * gas-to-nuclear (SMR) development strategy" against a declared 'no'
 * (the financial builder pre-dated the RNH-1 wiring; the SED's
 * legitimate Wyoming pro-nuclear context filled the vacuum).
 *
 * Shape: recommendation/advancement verb followed — within the same
 * sentence — by a nuclear-domain token. Comparative context rows
 * ("Nuclear SMR: RED risk · 5-7yr") carry no recommendation verb and do
 * not match; that is exactly the "comparative tables as context" carve-out
 * the stance text permits.
 */
const NUCLEAR_RECOMMENDATION_REGEX =
  /\b(?:recommend(?:s|ed|ing)?(?!\s+against)|pursu(?:e|ing)|adopt(?:s|ing)?|advanc(?:e|ing)|initiat(?:e|ing)|prioriti[sz](?:e|ing)|proceed(?:ing)?\s+with|commit(?:s|ting)?\s+to|begin(?:ning)?|fil(?:e|ing)|submit(?:ting)?|commission(?:s|ing)?)\b[^.!?\n]*?\b(?:SMR|small\s+modular\s+reactor|nuclear|ESP|COL|NRC\s+licen[cs]\w*|PSHA)\b/gi;

/**
 * Inline beacon for the `'not-considered'` lane — same bracketed shape
 * as `STANCE_SOFTEN_PREFIX` so the renderer / editor vocabulary stays
 * consistent.
 */
const STANCE_EXCLUDED_PREFIX = '[stance: not-considered — nuclear pathway excluded by project intent] ';

/**
 * True when the matched recommendation sits in a sentence that NEGATES
 * or EXCLUDES the nuclear pathway — "we do not recommend pursuing
 * nuclear", "nuclear was excluded per project intent", "rather than
 * pursuing an SMR". Those sentences are CORRECT prose under a 'no'
 * stance and must not be flagged. Mirrors `isWrappedInConditional`'s
 * bounded current-sentence lookback.
 */
const EXCLUSION_PREAMBLES: ReadonlyArray<RegExp> = [
  // Plain negation ("is not", "does not", "will not", bare "not") — a
  // sentence negating the recommendation IS the correct suppressive prose.
  /\b(?:not|no\s+longer|never|without)\b/i,
  /\b(?:exclud\w+|declin\w+|reject\w+|rul\w+\s+out|removed|suppress\w+)\b/i,
  /\b(?:rather\s+than|instead\s+of|as\s+opposed\s+to|in\s+lieu\s+of)\b/i,
];

function isExclusionContext(body: string, matchIdx: number): boolean {
  const lookbackStart = Math.max(0, matchIdx - 120);
  const lookback = body.slice(lookbackStart, matchIdx);
  // Clip at the last sentence boundary so a negation in a DIFFERENT
  // sentence cannot exempt this match (same clipping rule as
  // `isWrappedInConditional`).
  let currentSentence = lookback;
  let lastBoundary = -1;
  for (let i = lookback.length - 2; i >= 0; i--) {
    const ch = lookback[i];
    if (ch === '\n') {
      lastBoundary = i + 1;
      break;
    }
    if ((ch === '.' || ch === '!' || ch === '?') && /\s/.test(lookback[i + 1] ?? '')) {
      lastBoundary = i + 1;
      break;
    }
  }
  if (lastBoundary >= 0) {
    let j = lastBoundary;
    while (j < lookback.length && /[.!?\s]/.test(lookback[j] ?? '')) j++;
    currentSentence = lookback.slice(j);
  }
  return EXCLUSION_PREAMBLES.some(p => p.test(currentSentence));
}

/**
 * Per-lane guard configuration — regex to hunt, beacon prefix to plant,
 * and the exemption predicate that recognises the ACCEPTABLE form of
 * the matched prose for that lane.
 */
interface StanceGuardConfig {
  regex: RegExp;
  prefix: string;
  exempt: (body: string, matchIdx: number) => boolean;
}

const CONSIDERING_GUARD: StanceGuardConfig = {
  regex: ASSERTIVE_NUCLEAR_REGEX,
  prefix: STANCE_SOFTEN_PREFIX,
  exempt: isWrappedInConditional,
};

const NOT_CONSIDERED_GUARD: StanceGuardConfig = {
  regex: NUCLEAR_RECOMMENDATION_REGEX,
  prefix: STANCE_EXCLUDED_PREFIX,
  exempt: isExclusionContext,
};

function softenAssertiveInText(
  body: string,
  // RNH-2b — lane-specific regex + prefix + exemption. Defaults to the
  // 'considering' guard so existing call sites are byte-identical.
  guard: StanceGuardConfig = CONSIDERING_GUARD,
): { next: string; count: number } {
  if (!body) return { next: body, count: 0 };
  let count = 0;
  const re = new RegExp(guard.regex.source, guard.regex.flags);
  // `.replace` callback signature: (match, ...groups, offset, fullString).
  // Our regex has no capture groups, so the offset is the second arg.
  const replaced = body.replace(re, (m: string, offset: number) => {
    // Lane exemption — 'considering': the LLM produced the softened
    // "If pursuing …, immediately commission …" form RNH-1 asked for
    // (RNH-FOLLOWUP-CONDITIONAL-EXEMPT). 'not-considered': the sentence
    // negates / excludes the nuclear pathway, which IS the correct
    // suppressive prose. Leave it alone — no flag, no prefix.
    if (guard.exempt(body, offset)) return m;
    count++;
    // Idempotent: if the prefix is already present (a prior rerun),
    // don't stack a second one.
    if (m.startsWith(guard.prefix)) return m;
    return `${guard.prefix}${m}`;
  });
  return count > 0 ? { next: replaced, count } : { next: body, count: 0 };
}

function softenAssertiveValue(
  value: unknown,
  depth = 0,
  // RNH-2b — lane-specific guard, defaulting to 'considering'.
  guard: StanceGuardConfig = CONSIDERING_GUARD,
): { next: unknown; count: number } {
  if (value === null || value === undefined) return { next: value, count: 0 };
  if (typeof value === 'string') return softenAssertiveInText(value, guard);
  if (Array.isArray(value)) {
    if (depth >= 3) return { next: value, count: 0 };
    let total = 0;
    let mutated = false;
    const next = value.map(v => {
      const r = softenAssertiveValue(v, depth + 1, guard);
      if (r.count > 0) {
        mutated = true;
        total += r.count;
      }
      return r.next;
    });
    return mutated ? { next, count: total } : { next: value, count: 0 };
  }
  if (typeof value === 'object') {
    if (depth >= 3) return { next: value, count: 0 };
    let total = 0;
    let mutated = false;
    const next: Record<string, unknown> = { ...(value as Record<string, unknown>) };
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const r = softenAssertiveValue(v, depth + 1, guard);
      if (r.count > 0) {
        mutated = true;
        total += r.count;
        next[k] = r.next;
      }
    }
    return mutated ? { next, count: total } : { next: value, count: 0 };
  }
  return { next: value, count: 0 };
}

/**
 * RNH-2 — stance-mismatch post-gen guard over the report's narrative
 * surfaces. Behaviour is lane-specific:
 *
 * `'considering'` / `'undecided'` (same conservative lane):
 *   1. Tag any assertive nuclear RECOMMENDED ACTION inline with a
 *      `[stance: considering — soften before publish]` prefix.
 *   2. Stamp `report.qualityWarnings += [{ type: 'rnh-stance-mismatch',
 *      count, samples }]` so the renderer surfaces an amber chip
 *      ("Recommendations may not reflect your stance — review action
 *      plan") with an `[Open Rerun]` affordance.
 *
 * `'no'` → `'not-considered'` lane (RNH-2b, Pronghorn 2026-08-17):
 *   Any recommendation-shaped nuclear sentence — conditional or not —
 *   is a mismatch (the user EXCLUDED the pathway; gen-time RNH-1 should
 *   have suppressed it entirely). Same flag + warning mechanics with the
 *   `[stance: not-considered — nuclear pathway excluded by project
 *   intent]` prefix. This lane used to return identity on the theory
 *   that gen-time suppression made it a no-op — the Pronghorn financial
 *   report proved that assumption wrong whenever a builder misses the
 *   RNH-1 wiring, so the guard now acts as defense-in-depth.
 *
 * Passes through unchanged when:
 *   - `nuclearInterest` is `'primary'` (committed; assertive verbs OK).
 *   - No triggering patterns were found anywhere.
 *
 * Pure: returns identity when the guard didn't fire; a shallow-cloned
 * report with mutated subtrees + a new `qualityWarnings` array when
 * it did.
 *
 * MUST run after `applyClaimsEmission` so the post-gen claim shape
 * the renderer reads is stable. Order in the pipeline:
 *   generateNarrative → applyClaimsEmission → stripVerifierTags →
 *   flagAssertiveNuclearActions → persist.
 */
export function flagAssertiveNuclearActions(
  report: SiteRagReport,
  nuclearInterest: string | undefined | null,
): SiteRagReport {
  if (!report) return report;
  // Lane projection — matches `resolveNuclearStanceLane` in
  // reportPrompts.ts. `'primary'` keeps assertive verbs (no gating);
  // `'no'` gates ANY nuclear recommendation (RNH-2b); anything unknown
  // defaults to the conservative considering lane.
  const lane = nuclearInterest === 'primary'
    ? 'committed'
    : nuclearInterest === 'no'
    ? 'not-considered'
    : 'considering';
  if (lane === 'committed') return report;
  const guard = lane === 'not-considered' ? NOT_CONSIDERED_GUARD : CONSIDERING_GUARD;

  // Top-level string sections (executiveSummary, sitingNotes, summary)
  // and typed-object subtrees both checked.
  const flagged: Array<{ sectionId: string; phrase: string }> = [];
  const patch: Record<string, unknown> = {};
  let total = 0;

  for (const key of TOP_LEVEL_STRING_FIELDS) {
    const v = (report as unknown as Record<string, unknown>)[key];
    if (typeof v !== 'string') continue;
    collectAssertiveMatches(key, v, 0, flagged, guard);
    const r = softenAssertiveInText(v, guard);
    if (r.count > 0) {
      total += r.count;
      patch[key] = r.next;
    }
  }

  // Action-plan + recommendations live under typed-object subtrees
  // (developmentPathway[], recommendations[], plus the typed
  // per-section objects). Reuse the narrative-object list — those
  // are the same surfaces the strip walks.
  for (const key of NARRATIVE_OBJECT_FIELDS) {
    const v = (report as unknown as Record<string, unknown>)[key];
    collectAssertiveMatches(key, v, 0, flagged, guard);
    const r = softenAssertiveValue(v, 0, guard);
    if (r.count > 0) {
      total += r.count;
      patch[key] = r.next;
    }
  }

  // Also walk the action-plan adjacent shapes that aren't in the
  // narrative-object list above (they're top-level arrays).
  for (const key of ['developmentPathway', 'recommendations', 'appendix']) {
    const v = (report as unknown as Record<string, unknown>)[key];
    if (!Array.isArray(v)) continue;
    collectAssertiveMatches(key, v, 0, flagged, guard);
    const r = softenAssertiveValue(v, 0, guard);
    if (r.count > 0) {
      total += r.count;
      patch[key] = r.next;
    }
  }

  if (flagged.length === 0 && total === 0) return report;

  console.info(
    `[rnh:stance-mismatch] reportId=${report.id} count=${flagged.length} stance=${lane}`,
  );

  const existingWarnings = Array.isArray(report.qualityWarnings) ? report.qualityWarnings : [];
  const newWarning = {
    type: 'rnh-stance-mismatch' as const,
    count: flagged.length,
    samples: flagged.slice(0, 3),
    // RNH-2b — which lane fired, so the renderer chip can distinguish
    // "soften before publish" from "pathway excluded by intent".
    // Optional on the type; absent on pre-RNH-2b reports.
    stance: lane,
  };

  return {
    ...report,
    ...patch,
    qualityWarnings: [...existingWarnings, newWarning],
  } as SiteRagReport;
}

/** Exported for direct testing. */
export const __INTERNAL_RNH = {
  ASSERTIVE_NUCLEAR_REGEX,
  STANCE_SOFTEN_PREFIX,
  CONDITIONAL_PREAMBLES,
  isWrappedInConditional,
  // RNH-2b — 'not-considered' lane internals.
  NUCLEAR_RECOMMENDATION_REGEX,
  STANCE_EXCLUDED_PREFIX,
  EXCLUSION_PREAMBLES,
  isExclusionContext,
} as const;

/* ─── PSE-3 — Public-sentiment-conflict post-gen guard ─────────────── */

/**
 * Per-tech aggregated sentiment shape this guard reads. Structurally
 * compatible with `PerTechSentiment` from `types/siteEvidence` (the
 * orchestrator's output) — kept as a local interface so the guard
 * doesn't take a hard import dependency on the SED type module (the
 * shape is intentionally narrow: only `meanSentiment` + `count` are
 * consumed).
 */
export interface PerTechSentimentInput {
  /** BTM tech id (e.g. `'nuclear-smr'`, `'natural-gas'`). */
  btmTech: string;
  /** Confidence-weighted mean in [-1, +1]. < -0.2 ⇒ oppositional. */
  meanSentiment: number;
  /** Sample count. < 5 ⇒ insufficient evidence, guard sits out. */
  count: number;
}

/**
 * Per-tech assertive-recommendation regex table. Each entry matches a
 * confident verb ("recommend", "deploy", "select", "proceed with",
 * "commission") followed within ≤80 chars by a noun phrase that
 * identifies the tech. The 80-char ceiling discriminates "recommend
 * Solar+BESS as Phase 1" from "recommend a path that does NOT include
 * Solar+BESS for 18 months" — the negation cases are out of scope for
 * this guard and the conditional-preamble exemption (shared with RNH-2)
 * catches the politely-worded forms.
 *
 * Patterns are anchored on the LLM's preferred phrasing (observed
 * live-prod across Sickel + Redhead reports). Case-insensitive +
 * global so all matches are counted.
 */
const PSE_TECH_PATTERNS: Record<string, RegExp> = {
  'nuclear-smr':
    /\b(?:recommend(?:ed|s)?|deploy(?:ing)?|select(?:ed|s)?|proceed\s+with|commission(?:ing|ed)?|advance\s+to|pursue\s+the)\b[^.!?]{0,80}\b(?:SMR|nuclear|small\s+modular\s+reactor)\b/gi,
  'natural-gas':
    /\b(?:recommend(?:ed|s)?|deploy(?:ing)?|select(?:ed|s)?|proceed\s+with|commission(?:ing|ed)?|advance\s+to|pursue\s+the)\b[^.!?]{0,80}\b(?:natural\s+gas|gas\s+turbine|combined[-\s]?cycle|CCGT)\b/gi,
  'solar-bess':
    /\b(?:recommend(?:ed|s)?|deploy(?:ing)?|select(?:ed|s)?|proceed\s+with|commission(?:ing|ed)?|advance\s+to|pursue\s+the)\b[^.!?]{0,80}\b(?:solar(?:\s*\+\s*BESS)?|photovoltaic|PV(?:\s*\+\s*storage)?|utility[-\s]?scale\s+solar)\b/gi,
  'wind-bess':
    /\b(?:recommend(?:ed|s)?|deploy(?:ing)?|select(?:ed|s)?|proceed\s+with|commission(?:ing|ed)?|advance\s+to|pursue\s+the)\b[^.!?]{0,80}\b(?:wind(?:\s*\+\s*BESS)?|wind\s+turbine|wind\s+farm)\b/gi,
  'general-datacenter':
    /\b(?:recommend(?:ed|s)?|deploy(?:ing)?|select(?:ed|s)?|proceed\s+with|commission(?:ing|ed)?|advance\s+to|pursue\s+the)\b[^.!?]{0,80}\b(?:data\s*center|datacenter|hyperscale)\b/gi,
};

/**
 * Per-tech inline softening prefix. Mirrors `STANCE_SOFTEN_PREFIX`
 * (RNH-2) so the rendered chip layer + tooltip surface a uniform
 * pattern across the two adjacent guards. The verbatim phrase is left
 * intact for traceability.
 */
const SENTIMENT_SOFTEN_PREFIX = '[sentiment: negative — verify engagement plan before publish] ';

/**
 * Lower bound on the per-tech sample count before the guard fires.
 * Sentiment with < 5 entries is statistically thin enough that a
 * negative mean is more likely to reflect sampling noise than real
 * oppositional signal — softening the narrative on that basis would
 * over-fire. The PSE pack's R1 mitigation explicitly cites "confidence
 * cap at 0.6 when source-count < 10"; this guard is even more
 * conservative because a softened recommendation is a visible
 * intervention on the customer's report.
 */
const PSE_MIN_SAMPLE_COUNT = 5;

/**
 * Score threshold below which a tech's sentiment counts as
 * "oppositional" for purposes of softening. PSE pack §2.5 PSE-3 spec
 * uses `score < -0.2` for the original cutoff; the prompt brief asks
 * for `>0.6 oppositional signal` for the stronger gate. We use ≤ -0.2
 * for matching softness (matching the pack's threshold), and the
 * `oppositionalShare` chip on the renderer can layer a sharper
 * threshold for high-confidence flagging without touching the guard.
 */
const PSE_OPPOSITIONAL_THRESHOLD = -0.2;

/**
 * Walk a value and collect all assertive-recommendation matches for
 * `tech`. Pure. Same recursion pattern as
 * `collectAssertiveMatches` (RNH-2) but takes the tech-specific regex
 * + carries an offset for the conditional-preamble exemption.
 *
 *  - string         → exec the regex; skip matches inside a conditional
 *                     sentence; push every other match.
 *  - array          → recurse (depth-bounded).
 *  - object         → recurse one level (depth-bounded).
 *  - other          → ignore.
 *
 * Bounded depth (≤3) matches the RNH-2 collector — the narrative
 * subtree shapes are 2-deep at most (`btmDetailedAnalysis: [{...}]`),
 * so depth-3 fully covers them.
 */
function collectSentimentAssertiveMatches(
  tech: string,
  sectionId: string,
  value: unknown,
  re: RegExp,
  depth: number,
  acc: Array<{ tech: string; sectionId: string; phrase: string }>,
): void {
  if (value === null || value === undefined) return;
  if (typeof value === 'string') {
    // Fresh regex per call (regex state is mutable; sharing it across
    // recursion would skip matches via stale `.lastIndex`).
    const localRe = new RegExp(re.source, re.flags);
    let m: RegExpExecArray | null;
    while ((m = localRe.exec(value)) !== null) {
      if (isWrappedInConditional(value, m.index)) continue;
      // Idempotency — skip matches that were already softened by a
      // prior guard pass (same offset-prefix check as the softener).
      const prefixStart = m.index - SENTIMENT_SOFTEN_PREFIX.length;
      if (prefixStart >= 0 && value.slice(prefixStart, m.index) === SENTIMENT_SOFTEN_PREFIX) {
        continue;
      }
      // Cap the recorded phrase at 120 chars so the qualityWarnings
      // payload stays small and admin tooltip rendering is bounded.
      const phrase = m[0].length > 120 ? `${m[0].slice(0, 117)}...` : m[0];
      acc.push({ tech, sectionId, phrase });
    }
    return;
  }
  if (Array.isArray(value)) {
    if (depth >= 3) return;
    for (const v of value) collectSentimentAssertiveMatches(tech, sectionId, v, re, depth + 1, acc);
    return;
  }
  if (typeof value === 'object') {
    if (depth >= 3) return;
    for (const v of Object.values(value as Record<string, unknown>)) {
      collectSentimentAssertiveMatches(tech, sectionId, v, re, depth + 1, acc);
    }
  }
}

/**
 * Soften every assertive-recommendation match for `tech` in a string.
 * Mirrors `softenAssertiveInText` (RNH-2). Returns `{ next, count }`.
 * Idempotent — does not double-prefix.
 */
function softenSentimentInText(
  body: string,
  re: RegExp,
): { next: string; count: number } {
  if (!body) return { next: body, count: 0 };
  let count = 0;
  const localRe = new RegExp(re.source, re.flags);
  const replaced = body.replace(localRe, (m: string, offset: number) => {
    if (isWrappedInConditional(body, offset)) return m;
    // Idempotency check — if the prefix already appears immediately
    // before this match's offset, the guard ran on this body before
    // and we must NOT re-prepend. `body.slice(prefixStart, offset)`
    // catches the prefix-then-match-start shape regardless of where
    // the match itself begins (verb / noun-phrase).
    const prefixStart = offset - SENTIMENT_SOFTEN_PREFIX.length;
    if (prefixStart >= 0 && body.slice(prefixStart, offset) === SENTIMENT_SOFTEN_PREFIX) {
      return m;
    }
    count++;
    return `${SENTIMENT_SOFTEN_PREFIX}${m}`;
  });
  return count > 0 ? { next: replaced, count } : { next: body, count: 0 };
}

/**
 * Recursive softener — clones arrays/objects on mutation, identity on
 * clean. Same pattern as `softenAssertiveValue` (RNH-2).
 */
function softenSentimentValue(
  value: unknown,
  re: RegExp,
  depth: number,
): { next: unknown; count: number } {
  if (value === null || value === undefined) return { next: value, count: 0 };
  if (typeof value === 'string') return softenSentimentInText(value, re);
  if (Array.isArray(value)) {
    if (depth >= 3) return { next: value, count: 0 };
    let total = 0;
    let mutated = false;
    const next = value.map(v => {
      const r = softenSentimentValue(v, re, depth + 1);
      if (r.count > 0) {
        mutated = true;
        total += r.count;
      }
      return r.next;
    });
    return mutated ? { next, count: total } : { next: value, count: 0 };
  }
  if (typeof value === 'object') {
    if (depth >= 3) return { next: value, count: 0 };
    let total = 0;
    let mutated = false;
    const next: Record<string, unknown> = { ...(value as Record<string, unknown>) };
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const r = softenSentimentValue(v, re, depth + 1);
      if (r.count > 0) {
        mutated = true;
        total += r.count;
        next[k] = r.next;
      }
    }
    return mutated ? { next, count: total } : { next: value, count: 0 };
  }
  return { next: value, count: 0 };
}

/**
 * PSE-3 — post-generation guard that flags assertive recommendations
 * for BTM techs whose local public sentiment is oppositional.
 *
 * Sibling of `flagAssertiveNuclearActions` (RNH-2) — same softening
 * pattern, same `qualityWarnings` shape. The difference is the
 * gating axis:
 *   - RNH-2 gates on `projectIntent.nuclearInterest` (a static stance
 *     the user chose in the wizard).
 *   - PSE-3 gates on the live `publicSentiment.perTech` data the
 *     `publicSentimentOrchestrator` collected — sentiment is a moving
 *     target keyed to the SITE, not the user.
 *
 * Detection logic:
 *   1. For each tech in `perTechSentiment` whose `meanSentiment ≤ -0.2`
 *      AND `count ≥ PSE_MIN_SAMPLE_COUNT`, scan the narrative surfaces
 *      for assertive-recommendation phrases that name that tech.
 *   2. Matches inside a conditional preamble ("If sentiment improves…"
 *      / "Subject to community engagement…") are exempted via the
 *      shared `isWrappedInConditional` helper.
 *   3. Each non-exempt match is softened in place with the
 *      `SENTIMENT_SOFTEN_PREFIX` beacon (idempotent — repeats won't
 *      stack).
 *   4. `qualityWarnings` gains a `pse-sentiment-conflict` entry
 *      carrying up to 3 representative samples per tech.
 *
 * Returns identity (same object reference) when:
 *   - `perTechSentiment` is empty / null / undefined.
 *   - Every tech has `meanSentiment > PSE_OPPOSITIONAL_THRESHOLD` OR
 *     `count < PSE_MIN_SAMPLE_COUNT` (i.e. no oppositional bucket
 *     with enough samples to flag).
 *   - No assertive-recommendation patterns matched anywhere.
 *
 * MUST run after `applyClaimsEmission` and `stripVerifierTags` so the
 * post-gen claim shape + final prose are stable. Pipeline order:
 *   generateNarrative → applyClaimsEmission → stripVerifierTags →
 *   flagAssertiveNuclearActions → flagSentimentConflicts →
 *   stripMetaInstructions → persist.
 *
 * Defensive bounds:
 *   - Non-finite `meanSentiment` is treated as 0 (no flag).
 *   - Non-finite or negative `count` is treated as 0 (no flag).
 *   - Unknown tech ids (not in `PSE_TECH_PATTERNS`) are silently
 *     skipped — the orchestrator's bucket set is fixed at 5, but a
 *     future tech extension shouldn't break the guard.
 */
export function flagSentimentConflicts(
  report: SiteRagReport,
  perTechSentiment: ReadonlyArray<PerTechSentimentInput> | null | undefined,
): SiteRagReport {
  if (!report) return report;
  if (!perTechSentiment || perTechSentiment.length === 0) return report;

  // Resolve which techs are oppositional with sufficient evidence.
  // Numerically defensive — non-finite means the orchestrator may have
  // emitted a malformed bucket; treat as "no signal" rather than 0.
  const oppositional: Array<{ tech: string; sentimentScore: number }> = [];
  for (const t of perTechSentiment) {
    const score = Number.isFinite(t.meanSentiment) ? t.meanSentiment : 0;
    const count = Number.isFinite(t.count) && t.count > 0 ? t.count : 0;
    if (count < PSE_MIN_SAMPLE_COUNT) continue;
    if (score > PSE_OPPOSITIONAL_THRESHOLD) continue;
    if (!PSE_TECH_PATTERNS[t.btmTech]) continue;
    oppositional.push({ tech: t.btmTech, sentimentScore: score });
  }
  if (oppositional.length === 0) return report;

  // For each oppositional tech, walk every narrative surface and
  // collect matches + soften.
  const allSamples: Array<{ tech: string; sentimentScore: number; sectionId: string; phrase: string }> = [];
  const patchByKey: Record<string, unknown> = {};

  for (const { tech, sentimentScore } of oppositional) {
    const re = PSE_TECH_PATTERNS[tech];
    if (!re) continue;
    const collected: Array<{ tech: string; sectionId: string; phrase: string }> = [];

    for (const key of TOP_LEVEL_STRING_FIELDS) {
      const v = (report as unknown as Record<string, unknown>)[key];
      if (typeof v !== 'string') continue;
      collectSentimentAssertiveMatches(tech, key, v, re, 0, collected);
      const r = softenSentimentInText(v, re);
      if (r.count > 0) patchByKey[key] = r.next;
    }

    for (const key of NARRATIVE_OBJECT_FIELDS) {
      const v = (report as unknown as Record<string, unknown>)[key];
      collectSentimentAssertiveMatches(tech, key, v, re, 0, collected);
      const r = softenSentimentValue(v, re, 0);
      if (r.count > 0) patchByKey[key] = r.next;
    }

    // Also walk recommendations / developmentPathway / appendix — same
    // top-level arrays the RNH-2 guard treats specially.
    for (const key of ['developmentPathway', 'recommendations', 'appendix']) {
      const v = (report as unknown as Record<string, unknown>)[key];
      if (!Array.isArray(v)) continue;
      collectSentimentAssertiveMatches(tech, key, v, re, 0, collected);
      const r = softenSentimentValue(v, re, 0);
      if (r.count > 0) patchByKey[key] = r.next;
    }

    // Promote up to 3 representative samples per tech for the warning
    // payload. The full list is bounded by the guard at gen-time so
    // the payload stays small.
    for (const s of collected.slice(0, 3)) {
      allSamples.push({ tech: s.tech, sentimentScore, sectionId: s.sectionId, phrase: s.phrase });
    }
  }

  if (allSamples.length === 0) return report;

  // !cancelled gate is N/A here — this is a synchronous gen-time
  // pipeline step, not an async effect.
  console.info(
    `[pse:sentiment-conflict] reportId=${report.id} count=${allSamples.length} techs=${oppositional.map(o => o.tech).join(',')}`,
  );

  const existingWarnings = Array.isArray(report.qualityWarnings) ? report.qualityWarnings : [];
  const newWarning = {
    type: 'pse-sentiment-conflict' as const,
    count: allSamples.length,
    samples: allSamples.slice(0, 9),  // 3 techs × 3 samples upper bound
  };

  return {
    ...report,
    ...patchByKey,
    qualityWarnings: [...existingWarnings, newWarning],
  } as SiteRagReport;
}

/** PSE-3 — exported for direct testing. */
export const __INTERNAL_PSE = {
  PSE_TECH_PATTERNS,
  SENTIMENT_SOFTEN_PREFIX,
  PSE_MIN_SAMPLE_COUNT,
  PSE_OPPOSITIONAL_THRESHOLD,
} as const;

/* ─── PSE-4 — Alternative-option recommendation hook ──────────────── */

/**
 * PSE-4 — post-generation hook that takes the same `perTechSentiment`
 * payload PSE-3 reads and, when a recommended-action targets an
 * oppositional tech AND a sentiment-friendlier alt exists AND the alt
 * is plausible for the site profile, APPENDS a one-sentence advisory
 * to the offending recommended-action.
 *
 * Sibling of `flagSentimentConflicts` (PSE-3) — same gating axis, same
 * fixed BTM-tech set, same conservative thresholds. The difference is
 * the OUTPUT shape:
 *   - PSE-3 SOFTENS in-place + stamps `pse-sentiment-conflict`.
 *   - PSE-4 APPENDS the swap sentence + stamps
 *     `pse-alt-option-recommendation`.
 *
 * Pipeline order (per the pack §2.5):
 *   generateNarrative
 *     → applyClaimsEmission
 *     → stripVerifierTags
 *     → flagAssertiveNuclearActions
 *     → flagSentimentConflicts             (PSE-3 — soften)
 *     → applyAltOptionRecommendations      (PSE-4 — suggest alt)
 *     → stripMetaInstructions
 *     → persist
 *
 * Returns identity when:
 *   - No alt-option findings come back from
 *     `recommendAlternativeOptions`.
 *   - The narrative carries no targetable recommendation surface (the
 *     `recommendations[]` array + the top-level prose fields are both
 *     empty / missing).
 *
 * Idempotent — repeats won't stack the swap sentence (we check for the
 * sentence's stable opening clause "Alternatively, consider <alt>"
 * before appending).
 *
 * Defensive bounds:
 *   - `Number.isFinite()` strict guards already applied inside
 *     `recommendAlternativeOptions` — this hook trusts them.
 *   - Per-finding sentence is bounded by the composer; the warning
 *     payload caps `samples` at `PSE4_MAX_FINDINGS` (5).
 *   - NEVER throws — any defect in `recommendAlternativeOptions`
 *     (e.g. malformed perTechSentiment) becomes identity-return.
 */
export function applyAltOptionRecommendations(
  report: SiteRagReport,
  perTechSentiment: ReadonlyArray<PerTechSentimentInput> | null | undefined,
  siteProfile?: AltOptionSiteProfile,
): SiteRagReport {
  if (!report) return report;
  if (!perTechSentiment || perTechSentiment.length === 0) return report;

  // Build the rendered narrative text the recommender consumes. We
  // concatenate the top-level prose fields + the typed-object subtrees
  // we know carry recommendation prose so the recommender's
  // `narrativeRecommendsTech` / `narrativeMentionsTech` probes have a
  // single surface to match against.
  const narrativeParts: string[] = [];
  for (const key of TOP_LEVEL_STRING_FIELDS) {
    const v = (report as unknown as Record<string, unknown>)[key];
    if (typeof v === 'string' && v.length > 0) narrativeParts.push(v);
  }
  // `recommendations` is `Recommendation[]` — pull `description` +
  // `rationale` for the scanning surface.
  const recs = Array.isArray(report.recommendations) ? report.recommendations : [];
  for (const r of recs) {
    if (r && typeof r === 'object') {
      const desc = (r as { description?: unknown }).description;
      const rat = (r as { rationale?: unknown }).rationale;
      const arch = (r as { architecture?: unknown }).architecture;
      if (typeof desc === 'string') narrativeParts.push(desc);
      if (typeof rat === 'string') narrativeParts.push(rat);
      if (typeof arch === 'string') narrativeParts.push(arch);
    }
  }
  // `developmentPathway` carries description prose too.
  const dp = (report as unknown as { developmentPathway?: unknown }).developmentPathway;
  if (Array.isArray(dp)) {
    for (const step of dp) {
      if (step && typeof step === 'object') {
        const d = (step as { description?: unknown }).description;
        if (typeof d === 'string') narrativeParts.push(d);
      }
    }
  }
  const narrativeText = narrativeParts.join('\n\n');
  if (narrativeText.length === 0) return report;

  // Static import — the recommender is a leaf with no back-edge so
  // there's no cycle risk. The legacy `safeImport` pattern at the
  // siteRagService callsites is for lazy code-splitting; this module
  // is already part of the hygiene bundle.
  let findings: AltOptionFinding[] = [];
  try {
    findings = recommendAlternativeOptionsImpl({
      perTechSentiment,
      narrativeText,
      siteProfile,
    });
  } catch (e) {
    // NEVER throws — defective recommender bug must not fail the gen
    // pipeline. Log and return identity.
    console.warn('[pse:alt-option-recommendation-failed]', (e as Error)?.message ?? String(e));
    return report;
  }
  if (findings.length === 0) return report;

  // Build a from-tech → finding map for fast lookup inside the
  // recommendation-array walker below.
  const findingByFrom = new Map<string, AltOptionFinding>();
  for (const f of findings) findingByFrom.set(f.fromTech, f);

  // Append the swap sentence to the FIRST recommended-action whose
  // body matches an assertive-recommendation regex for the from-tech.
  // We prefer the structured `recommendations[]` array because that's
  // where the recommended actions live; if no recs[] entry matches we
  // fall back to appending into the corresponding top-level prose
  // (executiveSummary) — same surface PSE-3 softens.
  const appendedFor = new Set<string>();
  const patchRecs: SiteRagReport['recommendations'] | null = (() => {
    if (recs.length === 0) return null;
    let mutated = false;
    const next = recs.map(r => {
      if (!r || typeof r !== 'object') return r;
      const desc = typeof r.description === 'string' ? r.description : '';
      if (!desc) return r;
      for (const [fromTech, finding] of findingByFrom.entries()) {
        if (appendedFor.has(fromTech)) continue;
        const re = PSE_TECH_PATTERNS[fromTech];
        if (!re) continue;
        const localRe = new RegExp(re.source, re.flags);
        if (!localRe.test(desc)) continue;
        // Idempotency — don't re-append if the swap sentence's stable
        // opener already appears on this description.
        const opener = `Alternatively, consider ${altTechLabelOf(finding.toTech)},`;
        if (desc.includes(opener)) {
          appendedFor.add(fromTech);
          continue;
        }
        const sep = /[.!?]\s*$/.test(desc) ? ' ' : '. ';
        const nextDesc = `${desc}${sep}${finding.sentence}`;
        mutated = true;
        appendedFor.add(fromTech);
        return { ...r, description: nextDesc };
      }
      return r;
    });
    return mutated ? next : null;
  })();

  // Fallback: append to executiveSummary for any finding we didn't
  // place in `recommendations[]`. We append exactly one sentence per
  // finding, after the first assertive match in the body.
  let patchExecSummary: string | null = null;
  const stillUnplaced: typeof findings = [];
  for (const f of findings) {
    if (!appendedFor.has(f.fromTech)) stillUnplaced.push(f);
  }
  if (stillUnplaced.length > 0) {
    const body = typeof report.executiveSummary === 'string' ? report.executiveSummary : '';
    if (body.length > 0) {
      let next = body;
      let mutated = false;
      for (const f of stillUnplaced) {
        const re = PSE_TECH_PATTERNS[f.fromTech];
        if (!re) continue;
        const localRe = new RegExp(re.source, re.flags);
        if (!localRe.test(next)) continue;
        const opener = `Alternatively, consider ${altTechLabelOf(f.toTech)},`;
        if (next.includes(opener)) {
          appendedFor.add(f.fromTech);
          continue;
        }
        const sep = /[.!?]\s*$/.test(next) ? ' ' : '. ';
        next = `${next}${sep}${f.sentence}`;
        mutated = true;
        appendedFor.add(f.fromTech);
      }
      if (mutated) patchExecSummary = next;
    }
  }

  // Filter the warning samples down to findings we ACTUALLY appended
  // (don't surface a warning for a finding that had nowhere to land).
  const appliedFindings = findings.filter(f => appendedFor.has(f.fromTech));
  if (appliedFindings.length === 0) return report;

  console.info(
    `[pse:alt-option-applied] reportId=${report.id} count=${appliedFindings.length} ` +
    `swaps=${appliedFindings.map(f => `${f.fromTech}→${f.toTech}`).join(',')}`,
  );

  const existingWarnings = Array.isArray(report.qualityWarnings) ? report.qualityWarnings : [];
  const newWarning = {
    type: 'pse-alt-option-recommendation' as const,
    count: appliedFindings.length,
    samples: appliedFindings.map(f => ({
      fromTech: f.fromTech,
      toTech: f.toTech,
      fromSentiment: f.fromSentiment,
      toSentiment: f.toSentiment,
      positivePct: f.positivePct,
      negativePct: f.negativePct,
    })),
  };

  const patch: Partial<SiteRagReport> = {};
  if (patchRecs) patch.recommendations = patchRecs;
  if (patchExecSummary !== null) patch.executiveSummary = patchExecSummary;

  return {
    ...report,
    ...patch,
    qualityWarnings: [...existingWarnings, newWarning],
  } as SiteRagReport;
}

/**
 * Inline label resolver mirroring `altTechLabel` from the recommender.
 * Duplicated here to avoid a cyclic import at module-load time when the
 * recommender is required dynamically above. Keep the two label maps in
 * sync; they are deliberately tiny.
 */
function altTechLabelOf(tech: string): string {
  switch (tech) {
    case 'nuclear-smr': return 'nuclear SMR';
    case 'natural-gas': return 'natural gas';
    case 'solar-bess': return 'solar+BESS';
    case 'wind-bess': return 'wind+BESS';
    case 'general-datacenter': return 'a more flexible data-center configuration';
    default: return tech;
  }
}

/* ─── WPG-2 — workload-profile-drift post-gen guard ──────────────── */

/**
 * Workload Profile Gating WPG-2 — flag narrative prose that uses a
 * workload term inconsistent with `projectIntent.workloadProfile`.
 *
 * Live-prod 2026-05-18 (Sickel Feasibility v3.0): intent declared
 * `workloadProfile = 'ai-training'` but the Executive Summary read
 * *"For a 300 MW AI-inference data center focused on resilience…"*.
 * The LLM picked "inference" despite intent saying "training" because
 * the gen-time prompt didn't tell it which source to prefer (now fixed
 * upstream by WPG-1's `buildWorkloadCapacityGuidance`). This guard is
 * the post-gen safety net — it scans the final prose and flags any
 * residual drift so the renderer can surface an amber chip.
 *
 * The regex set is keyed by the AUTHORITATIVE workload id and matches
 * the DIFFERENT workload's noun-phrase. Drift only fires when the LLM
 * used a term FROM A DIFFERENT BUCKET than intent:
 *   - intent ai-training   + prose "AI-inference (data)center"  → flag
 *   - intent ai-inference  + prose "AI-training (data)center"   → flag
 *   - intent hpc           + prose "AI-(training|inference) (data)center" → flag
 *   - intent enterprise    + prose "AI-(training|inference) (data)center" → flag
 *   - intent colocation    + prose "AI-(training|inference) (data)center" → flag
 *   - intent edge          + prose "AI-(training|inference) (data)center" → flag
 *   - intent mixed         → no flag (mixed expressly contemplates all)
 *
 * Risk-R1 mitigation: the pattern requires "data ?center" or
 * "datacenter" within ≤4 words of the term — discriminates the
 * noun-phrase from generic mention ("comparable to AI-inference
 * workloads"). Cross-reference sentences inside comparative tables
 * stay legal.
 */
const WORKLOAD_DRIFT_PATTERNS: Record<string, RegExp> = {
  // AI training intent → flag AI-inference noun-phrase.
  'ai-training': /\bAI[- ]inference(?:\s+\w+){0,3}\s+data\s?center\b|\bAI[- ]inference\s+datacenter\b/gi,
  // AI inference intent → flag AI-training noun-phrase.
  'ai-inference': /\bAI[- ]training(?:\s+\w+){0,3}\s+data\s?center\b|\bAI[- ]training\s+datacenter\b/gi,
  // HPC / enterprise / colocation / edge intent → flag EITHER AI noun-
  // phrase. (Reduces false-positive surface: HPC docs may legitimately
  // mention AI workloads as comparable density, but the headline noun-
  // phrase describing THIS project shouldn't be AI-anything.)
  'hpc': /\bAI[- ](?:training|inference)(?:\s+\w+){0,3}\s+data\s?center\b|\bAI[- ](?:training|inference)\s+datacenter\b/gi,
  'enterprise': /\bAI[- ](?:training|inference)(?:\s+\w+){0,3}\s+data\s?center\b|\bAI[- ](?:training|inference)\s+datacenter\b/gi,
  'colocation': /\bAI[- ](?:training|inference)(?:\s+\w+){0,3}\s+data\s?center\b|\bAI[- ](?:training|inference)\s+datacenter\b/gi,
  'edge': /\bAI[- ](?:training|inference)(?:\s+\w+){0,3}\s+data\s?center\b|\bAI[- ](?:training|inference)\s+datacenter\b/gi,
  // `mixed` intent expressly contemplates either / both — no flag.
} as const;

export interface WorkloadDriftMatch {
  /** The narrative surface where the drift was found. */
  sectionId: string;
  /** The matched phrase verbatim. */
  phrase: string;
}

/**
 * Walk a value and collect every workload-drift match. Mirrors the
 * recursion pattern in `collectAssertiveMatches` so the surfaces walked
 * stay in lockstep across guards.
 *
 * The regex is reset per call (fresh RegExp from .source/.flags) — the
 * shared global-flag pattern is not safe to share statefully.
 */
function collectWorkloadDriftMatches(
  sectionId: string,
  value: unknown,
  pattern: RegExp,
  depth = 0,
  acc: WorkloadDriftMatch[] = [],
): WorkloadDriftMatch[] {
  if (value === null || value === undefined) return acc;
  if (typeof value === 'string') {
    const re = new RegExp(pattern.source, pattern.flags);
    let m: RegExpExecArray | null;
    while ((m = re.exec(value)) !== null) {
      acc.push({ sectionId, phrase: m[0] });
    }
    return acc;
  }
  if (Array.isArray(value)) {
    if (depth >= 3) return acc;
    for (const v of value) collectWorkloadDriftMatches(sectionId, v, pattern, depth + 1, acc);
    return acc;
  }
  if (typeof value === 'object') {
    if (depth >= 3) return acc;
    for (const v of Object.values(value as Record<string, unknown>)) {
      collectWorkloadDriftMatches(sectionId, v, pattern, depth + 1, acc);
    }
    return acc;
  }
  return acc;
}

/**
 * WPG-2 — flag workload-vocabulary drift across the report's narrative
 * surfaces.
 *
 * Inputs:
 *  - `report` — the persisted shape (post-CSE, post-RNH-5).
 *  - `workloadProfile` — canonical id from
 *    `effectiveWorkloadProfile(intent)`. Pass undefined / unknown to
 *    skip the guard entirely (Risk-R7 mitigation: legacy sites with no
 *    workloadProfile field).
 *
 * Returns the (possibly identity) report. When drift is found,
 * stamps `report.qualityWarnings += [{ type: 'wpg-workload-drift',
 * count, samples }]`. Pure — never mutates the body, only annotates.
 *
 * Ordering in the gen pipeline (see siteRagService.ts):
 *   generateNarrative → applyClaimsEmission → stripVerifierTags →
 *   flagAssertiveNuclearActions → stripMetaInstructions →
 *   flagWorkloadProfileDrift → flagCapacityDrift → persist.
 *
 * Body is intentionally NOT softened (unlike RNH-2's stance guard).
 * The right fix for vocabulary drift is regeneration via WPG-1's
 * gen-time block, not in-place substitution — automated swap risks
 * mangling agreement / determiners across the sentence. The amber chip
 * + sample list lets the user decide whether to rerun.
 */
export function flagWorkloadProfileDrift(
  report: SiteRagReport,
  workloadProfile: string | undefined | null,
): SiteRagReport {
  if (!report) return report;
  if (!workloadProfile) return report;
  const pattern = WORKLOAD_DRIFT_PATTERNS[workloadProfile];
  // `mixed` intentionally has no pattern — no flag fires.
  if (!pattern) return report;

  const flagged: WorkloadDriftMatch[] = [];
  for (const key of TOP_LEVEL_STRING_FIELDS) {
    const v = (report as unknown as Record<string, unknown>)[key];
    if (typeof v !== 'string') continue;
    collectWorkloadDriftMatches(key, v, pattern, 0, flagged);
  }
  for (const key of NARRATIVE_OBJECT_FIELDS) {
    const v = (report as unknown as Record<string, unknown>)[key];
    collectWorkloadDriftMatches(key, v, pattern, 0, flagged);
  }
  for (const key of ['developmentPathway', 'recommendations', 'appendix'] as const) {
    const v = (report as unknown as Record<string, unknown>)[key];
    if (!Array.isArray(v)) continue;
    collectWorkloadDriftMatches(key, v, pattern, 0, flagged);
  }

  if (flagged.length === 0) return report;

  console.info(
    `[wpg:workload-drift] reportId=${report.id} count=${flagged.length} intent=${workloadProfile}`,
  );

  const existingWarnings = Array.isArray(report.qualityWarnings) ? report.qualityWarnings : [];
  const newWarning = {
    type: 'wpg-workload-drift' as const,
    count: flagged.length,
    samples: flagged.slice(0, 3),
    intentProfile: workloadProfile,
  };

  return {
    ...report,
    qualityWarnings: [...existingWarnings, newWarning],
  } as SiteRagReport;
}

/* ─── WPG-3 — capacity-drift post-gen guard ──────────────────────── */

/**
 * Match a narrative MW figure. Captures the integer / decimal value so
 * the guard can compare against `financialCapacityMW`. Two-digit
 * minimum on the integer matches the live-prod scope ("10 MW" through
 * "9999 MW") and intentionally rules out single-digit numbers in
 * adjacent prose (e.g. "the 3 MW pilot" — those are reasonable
 * cross-references, not headline figures).
 *
 * The MW unit token allows "MW", "Megawatts" (case-insensitive). The
 * trailing word boundary keeps it from matching "300 MWh" (energy, not
 * power).
 */
const CAPACITY_NUMBER_PATTERN = /\b(\d{2,5}(?:\.\d+)?)\s*(?:MW(?!h)|megawatts?)\b/gi;

/**
 * Phrases that, when present within ±60 chars of a capacity match,
 * indicate the sentence IS already an explicit reconciliation note
 * (Risk-R2 mitigation per the pack). The guard skips the flag in those
 * cases so the WPG-1 gen-time rule's preferred output (reconciliation
 * sentence) does NOT trigger a post-gen warning on its own.
 *
 * The first three are reconciliation-cue tokens; the last three are
 * "contrast" markers that, when paired with one of the first three,
 * signal explicit two-sided framing.
 */
const CAPACITY_RECONCILIATION_TOKENS: ReadonlyArray<RegExp> = [
  /\bfinancial assumptions\b/i,
  /\bfinancial model\b/i,
  /\bproject intent\b/i,
  /\breconcile\b/i,
  /\bdivergen[ct]\w*\b/i,
];

const CAPACITY_CONTRAST_TOKENS: ReadonlyArray<RegExp> = [
  /\bvs\.?\b/i,
  /\bversus\b/i,
  /\bbut\b/i,
  /\bwhile\b/i,
  /\bwhereas\b/i,
];

/**
 * Inspect ±60 chars around a capacity match — when at least ONE
 * reconciliation token AND ONE contrast token co-occur in the window,
 * treat the figure as already-contextualised and skip the flag.
 *
 * Pure / side-effect free. Bounded lookups so we don't pay quadratic
 * cost on long bodies.
 */
function isCapacityContextualised(body: string, matchIdx: number, matchLen: number): boolean {
  const winStart = Math.max(0, matchIdx - 60);
  const winEnd = Math.min(body.length, matchIdx + matchLen + 60);
  const window = body.slice(winStart, winEnd);
  const hasReconciliation = CAPACITY_RECONCILIATION_TOKENS.some(p => p.test(window));
  if (!hasReconciliation) return false;
  const hasContrast = CAPACITY_CONTRAST_TOKENS.some(p => p.test(window));
  return hasContrast;
}

export interface CapacityDriftMatch {
  /** The narrative surface where the drift was found. */
  sectionId: string;
  /** The full matched phrase (e.g. "300 MW"). */
  phrase: string;
  /** Parsed numeric value of the match in MW. */
  mw: number;
}

/**
 * Walk a narrative string and collect capacity-drift matches that:
 *   (a) parse to within ±5% of the financial figure, AND
 *   (b) fall OUTSIDE the intent range by >5%, AND
 *   (c) are NOT inside a context-aware reconciliation window
 *       (Risk-R2 exemption).
 *
 * The same 5% tolerance threshold WPG-1's gen-time prompt uses, so the
 * two sides agree on what counts as a divergent narrative figure.
 */
function collectCapacityDriftInText(
  sectionId: string,
  body: string,
  intentMin: number,
  intentMax: number,
  financialMw: number,
  acc: CapacityDriftMatch[],
): void {
  const re = new RegExp(CAPACITY_NUMBER_PATTERN.source, CAPACITY_NUMBER_PATTERN.flags);
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    const captured = m[1];
    const mw = Number(captured);
    if (!Number.isFinite(mw) || mw <= 0) continue;
    // (a) Match the financial figure (±5%).
    const matchesFinancial = mw >= financialMw * 0.95 && mw <= financialMw * 1.05;
    if (!matchesFinancial) continue;
    // (b) Outside the intent range by >5%.
    const insideIntent = mw >= intentMin * 0.95 && mw <= intentMax * 1.05;
    if (insideIntent) continue;
    // (c) Risk-R2 exemption — already a reconciliation sentence.
    if (isCapacityContextualised(body, m.index ?? 0, m[0].length)) continue;
    acc.push({ sectionId, phrase: m[0], mw });
  }
}

function collectCapacityDriftValue(
  sectionId: string,
  value: unknown,
  intentMin: number,
  intentMax: number,
  financialMw: number,
  depth: number,
  acc: CapacityDriftMatch[],
): void {
  if (value === null || value === undefined) return;
  if (typeof value === 'string') {
    collectCapacityDriftInText(sectionId, value, intentMin, intentMax, financialMw, acc);
    return;
  }
  if (Array.isArray(value)) {
    if (depth >= 3) return;
    for (const v of value) collectCapacityDriftValue(sectionId, v, intentMin, intentMax, financialMw, depth + 1, acc);
    return;
  }
  if (typeof value === 'object') {
    if (depth >= 3) return;
    for (const v of Object.values(value as Record<string, unknown>)) {
      collectCapacityDriftValue(sectionId, v, intentMin, intentMax, financialMw, depth + 1, acc);
    }
  }
}

/**
 * WPG-3 — flag narrative capacity figures that match the financial
 * assumption while falling outside the Project Intent range.
 *
 * Inputs:
 *  - `report` — persisted shape (post-CSE, post-RNH-5, post-MEL,
 *    post-WPG-2).
 *  - `intent` — `{ min, max }` MW range parsed from the intent
 *    `powerRequirement` bucket OR the exact `powerRequirementMW`. Pass
 *    `null` to skip the guard (Risk-R4: legacy / undecided intent).
 *  - `financialCapacityMW` — financial-assumptions figure. Pass
 *    undefined to skip (no FA → no divergence possible).
 *  - `intentPowerLabel` — display label included in the warning entry
 *    so the renderer chip can surface BOTH sides verbatim.
 *
 * Returns the (possibly identity) report. Stamps
 * `qualityWarnings: [{ type: 'wpg-capacity-drift', count, samples, intentPowerLabel, financialCapacityMW }]`
 * when at least one drift match was found that wasn't already
 * context-aware-exempt.
 *
 * Pure — body is never mutated. The renderer chip links to the
 * existing Financial Model `[⊕ SYNC TO INTENT]` button so the user can
 * resolve at the source.
 *
 * Ordering: runs AFTER `flagWorkloadProfileDrift` so per-section
 * quality warnings stack predictably.
 */
export function flagCapacityDrift(
  report: SiteRagReport,
  intent: { min: number; max: number } | null | undefined,
  financialCapacityMW: number | undefined | null,
  intentPowerLabel: string | undefined | null,
): SiteRagReport {
  if (!report) return report;
  // Skip when either side is missing — no divergence possible.
  if (intent == null) return report;
  if (typeof financialCapacityMW !== 'number' || !Number.isFinite(financialCapacityMW) || financialCapacityMW <= 0) return report;
  // Pre-check: financial within intent → no flag at all. Cheap to
  // short-circuit before walking the body.
  const inIntent =
    financialCapacityMW >= intent.min * 0.95
    && financialCapacityMW <= intent.max * 1.05;
  if (inIntent) return report;

  const flagged: CapacityDriftMatch[] = [];
  for (const key of TOP_LEVEL_STRING_FIELDS) {
    const v = (report as unknown as Record<string, unknown>)[key];
    if (typeof v !== 'string') continue;
    collectCapacityDriftInText(key, v, intent.min, intent.max, financialCapacityMW, flagged);
  }
  for (const key of NARRATIVE_OBJECT_FIELDS) {
    const v = (report as unknown as Record<string, unknown>)[key];
    collectCapacityDriftValue(key, v, intent.min, intent.max, financialCapacityMW, 0, flagged);
  }
  for (const key of ['developmentPathway', 'recommendations', 'appendix'] as const) {
    const v = (report as unknown as Record<string, unknown>)[key];
    if (!Array.isArray(v)) continue;
    collectCapacityDriftValue(key, v, intent.min, intent.max, financialCapacityMW, 0, flagged);
  }

  if (flagged.length === 0) return report;

  console.info(
    `[wpg:capacity-drift] reportId=${report.id} count=${flagged.length} financial=${financialCapacityMW} intent=[${intent.min},${intent.max}]`,
  );

  const existingWarnings = Array.isArray(report.qualityWarnings) ? report.qualityWarnings : [];
  const newWarning = {
    type: 'wpg-capacity-drift' as const,
    count: flagged.length,
    samples: flagged.slice(0, 3),
    intentPowerLabel: intentPowerLabel ?? '',
    financialCapacityMW,
  };

  return {
    ...report,
    qualityWarnings: [...existingWarnings, newWarning],
  } as SiteRagReport;
}

/** Exported for direct testing. */
export const __INTERNAL_WPG = {
  WORKLOAD_DRIFT_PATTERNS,
  CAPACITY_NUMBER_PATTERN,
  CAPACITY_RECONCILIATION_TOKENS,
  CAPACITY_CONTRAST_TOKENS,
  isCapacityContextualised,
} as const;

/* ─── MEL — Meta-instruction leak strip ──────────────────────────── */

/**
 * Patterns that indicate the LLM's QA-review iteration loop emitted a
 * self-directive rather than narrative.
 *
 * **Reputation-risk hotfix (2026-05-18).** Live-prod Sickel Feasibility
 * v2.0 (the FRESH rerun after Wave-6 RNH shipped) revealed the
 * Executive Summary body persisted as:
 *
 *   "The analysis should be framed around the 50-200 MW requirement
 *    from the Project Intent, noting the 300 MW figure from financial
 *    assumptions as a potential scaling target."
 *
 * That is the reviewer's correction-directive-to-itself, not a
 * summary. Any reader sees the AI talking to itself instead of an
 * executive summary — instant trust collapse.
 *
 * The patterns below are deliberately narrow: they target the
 * *imperative-toward-the-system* shape ("the analysis should be
 * framed", "reframe to", "the section should focus") and NOT pure
 * declarative-about-the-site sentences ("The water resources question
 * is framed by Aqueduct's score of 0.48 …"). False-positive cases are
 * pinned by regression tests.
 *
 * Family tag is emitted alongside the match so admin telemetry can
 * roll up by family without leaking the raw excerpt by default.
 */
const META_INSTRUCTION_PATTERNS: ReadonlyArray<{ regex: RegExp; family: string }> = [
  // "(The) analysis should/must be framed around/in terms of …"
  { regex: /\b(?:the\s+)?analysis\s+(?:should|must)\s+be\s+framed\s+(?:around|in\s+terms\s+of)\b/i, family: 'framing-directive' },
  // "(The) narrative should/must note/reflect/incorporate …"
  { regex: /\b(?:the\s+)?narrative\s+(?:should|must)\s+(?:note|reflect|incorporate|mention)\b/i, family: 'narrative-directive' },
  // "(This) section should/must focus/cover/address …"
  { regex: /\b(?:this\s+)?section\s+(?:should|must)\s+(?:focus|cover|address)\b/i, family: 'section-directive' },
  // "Reframe/rephrase/rewrite/restructure this|the section/paragraph/analysis …"
  { regex: /\b(?:reframe|rephrase|rewrite|restructure)\s+(?:this|the)\s+(?:section|paragraph|analysis)\b/i, family: 'rewrite-directive' },
  // "noting the <N MW/value> figure from financial/project assumptions/intent …"
  { regex: /\bnoting\s+the\s+[^.]{0,40}\b(?:figure|value|number|requirement)\s+(?:from|in)\s+(?:financial|project)\s+(?:assumptions|intent)\b/i, family: 'figure-reconciliation-directive' },
  // "(The) prose should/must …"
  { regex: /\b(?:the\s+)?prose\s+(?:should|must)\b/i, family: 'prose-directive' },
  // "(The) summary/section/conclusion/… should be revised/expanded/improved …"
  // Self-revision / QA-critique directives (the Redhead Project-Intent leak).
  // Anchored to TEXT nouns so a physical recommendation ("the grid should be
  // expanded", "the foundation should be strengthened") is never matched.
  { regex: /\b(?:the\s+)?(?:summary|section|analysis|narrative|prose|report|conclusion|overview|abstract|write-?up)\s+(?:should|must|needs to|ought to)\s+(?:be\s+)?(?:revised|rewritten|reworded|reworked|expanded|condensed|shortened|strengthened|improved|clarified|tightened|polished|reframed|made\s+more)\b/i, family: 'revision-directive' },
  // "Ensure (the) narrative/prose/section mentions/cites/references …"
  { regex: /\bensure\s+(?:the\s+)?(?:narrative|prose|section)\s+(?:mentions|cites|references)\b/i, family: 'ensure-directive' },
  // Reviewer-pipeline correctedSections meta-leaks — internal field
  // names that must never appear in customer-facing prose.
  { regex: /\bUse\s+the\s+text\s+provided\s+in\s+correctedSections\.\w+/i, family: 'corrected-sections-leak' },
  { regex: /\bprovided\s+in\s+the\s+['"][^'"]*correctedSections\.\w+[^'"]*['"]\s+field\b/i, family: 'corrected-sections-leak' },
  { regex: /\bcorrectedSections\.\w+/i, family: 'corrected-sections-leak' },
  // "A full executive summary should be provided. Example: This report …"
  { regex: /\b(?:a|an|the)\s+(?:full\s+)?executive summary\s+should\s+be\s+provided\b/i, family: 'executive-summary-directive' },
  { regex: /\bExample:\s*This report\b/i, family: 'prompt-example-echo' },
];

export interface MetaInstructionMatch {
  /** The narrative surface where the directive was found (e.g.
   *  `'executiveSummary'`, `'powerAnalysis'`, `'btmDetailedAnalysis'`). */
  sectionId: string;
  /** Family tag from `META_INSTRUCTION_PATTERNS` — admin rollups read
   *  this, raw excerpts stay on the audit log. */
  family: string;
  /** Up to ~180 chars of context around the matched directive. Used
   *  for the strip pass to find the offending sentence; logged in
   *  truncated form for telemetry. */
  excerpt: string;
  /** Index of the match inside the source string — used by the strip
   *  pass to delete the containing sentence. */
  index: number;
  /** Length of the match — sentence-boundary search uses this. */
  matchLength: number;
}

/**
 * Find every meta-instruction phrase in a single narrative string.
 * Pure / no side effects.
 *
 * Returns matches in order of appearance. The caller walks the report
 * structure; this helper only handles the one-string case.
 */
function findMetaInstructionsInText(
  sectionId: string,
  body: string,
): MetaInstructionMatch[] {
  if (!body) return [];
  const out: MetaInstructionMatch[] = [];
  for (const { regex, family } of META_INSTRUCTION_PATTERNS) {
    // Fresh regex per call — these patterns are not global, but be
    // defensive against future additions that set the `g` flag.
    const re = new RegExp(regex.source, regex.flags);
    const m = re.exec(body);
    if (!m) continue;
    const idx = m.index ?? 0;
    out.push({
      sectionId,
      family,
      excerpt: body.slice(Math.max(0, idx - 20), idx + 160).trim(),
      index: idx,
      matchLength: m[0].length,
    });
  }
  return out;
}

/**
 * Walk a value and collect every meta-instruction match. Mirrors the
 * walk in `collectAssertiveMatches` (RNH-2) so the strip surface stays
 * in sync with the detection surface.
 */
function collectMetaInstructionMatches(
  sectionId: string,
  value: unknown,
  depth = 0,
  acc: MetaInstructionMatch[] = [],
): MetaInstructionMatch[] {
  if (value === null || value === undefined) return acc;
  if (typeof value === 'string') {
    for (const hit of findMetaInstructionsInText(sectionId, value)) acc.push(hit);
    return acc;
  }
  if (Array.isArray(value)) {
    if (depth >= 3) return acc;
    for (const v of value) collectMetaInstructionMatches(sectionId, v, depth + 1, acc);
    return acc;
  }
  if (typeof value === 'object') {
    if (depth >= 3) return acc;
    for (const v of Object.values(value as Record<string, unknown>)) {
      collectMetaInstructionMatches(sectionId, v, depth + 1, acc);
    }
    return acc;
  }
  return acc;
}

/**
 * Detect meta-instruction leaks across the report's narrative
 * surfaces. Pure — does not mutate the input.
 *
 * Use this before persistence to decide whether to (a) strip the
 * offending sentences and stamp a `qualityWarnings` entry, or (b)
 * leave the report untouched when no leaks were found.
 *
 * Mirrors the surfaces stripped by RNH-5 (`TOP_LEVEL_STRING_FIELDS` +
 * `NARRATIVE_OBJECT_FIELDS`) plus the action-plan adjacent shapes
 * walked by RNH-2.
 */
export function detectMetaInstructions(report: SiteRagReport): MetaInstructionMatch[] {
  if (!report) return [];
  const matches: MetaInstructionMatch[] = [];

  for (const key of TOP_LEVEL_STRING_FIELDS) {
    const v = (report as unknown as Record<string, unknown>)[key];
    if (typeof v !== 'string') continue;
    for (const hit of findMetaInstructionsInText(key, v)) matches.push(hit);
  }
  for (const key of NARRATIVE_OBJECT_FIELDS) {
    const v = (report as unknown as Record<string, unknown>)[key];
    collectMetaInstructionMatches(key, v, 0, matches);
  }
  for (const key of ['developmentPathway', 'recommendations', 'appendix'] as const) {
    const v = (report as unknown as Record<string, unknown>)[key];
    if (!Array.isArray(v)) continue;
    collectMetaInstructionMatches(key, v, 0, matches);
  }

  return matches;
}

/**
 * Sentence-boundary aware strip for a single narrative string.
 *
 * The strategy: when a meta-instruction match is found inside a body,
 * remove the *entire enclosing sentence*. Half-stripping (just the
 * directive verb) would leave a fragment behind ("around the 50-200
 * MW requirement, noting …") which reads worse than removing the
 * whole sentence outright.
 *
 * Sentence boundaries are approximated by `[.!?]\s+` (closest left
 * boundary), or start-of-string when no boundary exists earlier. The
 * right boundary is the first `[.!?]\s+` AFTER the match (or
 * end-of-string).
 *
 * Returns `{ next, count }` where `count` is the number of sentences
 * removed (which equals the number of distinct matches when patterns
 * don't overlap).
 *
 * Pure / no side effects.
 */
function stripMetaInstructionsFromText(body: string): { next: string; count: number } {
  if (!body) return { next: body, count: 0 };
  const hits = findMetaInstructionsInText('__local__', body);
  if (hits.length === 0) return { next: body, count: 0 };

  // Compute sentence ranges to delete. Each range is [start, end) in
  // the ORIGINAL string coordinates. Deduplicate overlapping ranges
  // (two patterns hitting the same sentence count as one strip).
  const ranges: Array<{ start: number; end: number }> = [];
  for (const hit of hits) {
    const start = findSentenceStart(body, hit.index);
    const end = findSentenceEnd(body, hit.index + hit.matchLength);
    // Merge with the most recent range if they overlap (sorted by
    // appearance, so a simple "extend last" works).
    const last = ranges[ranges.length - 1];
    if (last && start <= last.end) {
      last.end = Math.max(last.end, end);
    } else {
      ranges.push({ start, end });
    }
  }

  // Apply deletions right-to-left so earlier indices stay valid.
  let next = body;
  for (let i = ranges.length - 1; i >= 0; i--) {
    const { start, end } = ranges[i] as { start: number; end: number };
    next = next.slice(0, start) + next.slice(end);
  }

  next = tightenPunctuation(next).trim();
  return { next, count: ranges.length };
}

/** Closest sentence-start boundary at or before `idx`. */
function findSentenceStart(body: string, idx: number): number {
  // Walk back to the first `[.!?]\s` pair (or `\n`). Returns the
  // index of the FIRST character of the sentence containing `idx`.
  for (let i = idx - 1; i >= 0; i--) {
    const ch = body[i];
    if (ch === '\n') return i + 1;
    if ((ch === '.' || ch === '!' || ch === '?') && /\s/.test(body[i + 1] ?? '')) {
      // Skip past the whitespace following the punctuation.
      let j = i + 1;
      while (j < body.length && /\s/.test(body[j] ?? '')) j++;
      return j;
    }
  }
  return 0;
}

/** Closest sentence-end boundary at or after `idx`. */
function findSentenceEnd(body: string, idx: number): number {
  for (let i = idx; i < body.length; i++) {
    const ch = body[i];
    if (ch === '\n') return i + 1;
    if (ch === '.' || ch === '!' || ch === '?') {
      // Include the punctuation + any trailing whitespace so the
      // surrounding sentences collapse cleanly.
      let j = i + 1;
      while (j < body.length && /\s/.test(body[j] ?? '')) j++;
      return j;
    }
  }
  return body.length;
}

/**
 * Walk a value and strip meta-instruction sentences from every string
 * leaf. Mirrors `stripFromValue` (RNH-5) so the strip surface stays
 * in sync.
 */
function stripMetaFromValue(value: unknown, depth = 0): { next: unknown; count: number } {
  if (value === null || value === undefined) return { next: value, count: 0 };
  if (typeof value === 'string') return stripMetaInstructionsFromText(value);
  if (Array.isArray(value)) {
    if (depth >= 3) return { next: value, count: 0 };
    let total = 0;
    let mutated = false;
    const nextArr = value.map(v => {
      const r = stripMetaFromValue(v, depth + 1);
      if (r.count > 0) {
        total += r.count;
        mutated = true;
      }
      return r.next;
    });
    return mutated ? { next: nextArr, count: total } : { next: value, count: 0 };
  }
  if (typeof value === 'object') {
    if (depth >= 3) return { next: value, count: 0 };
    let total = 0;
    let mutated = false;
    const nextObj: Record<string, unknown> = { ...(value as Record<string, unknown>) };
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const r = stripMetaFromValue(v, depth + 1);
      if (r.count > 0) {
        total += r.count;
        mutated = true;
        nextObj[k] = r.next;
      }
    }
    return mutated ? { next: nextObj, count: total } : { next: value, count: 0 };
  }
  return { next: value, count: 0 };
}

/**
 * MEL — strip meta-instruction sentences from every narrative surface
 * AND stamp `report.qualityWarnings += [{ type: 'meta-instruction-leak',
 * count, samples }]` so the renderer can flag affected sections for
 * manual review even after the strip.
 *
 * Pure: returns identity when no leaks were found anywhere; a
 * shallow-cloned report with mutated subtrees + a new
 * `qualityWarnings` array when at least one leak was stripped.
 *
 * MUST run AFTER `applyClaimsEmission` and AFTER `stripVerifierTags`
 * (the verifier tags can otherwise leak into the meta-instruction
 * detection surface), and BEFORE the function returns / persists.
 *
 * Order in the gen pipeline:
 *   generateNarrative → applyClaimsEmission → stripVerifierTags →
 *   flagAssertiveNuclearActions → stripMetaInstructions → persist.
 */
export function stripMetaInstructions(report: SiteRagReport): SiteRagReport {
  if (!report) return report;

  const allMatches: MetaInstructionMatch[] = [];
  const patch: Record<string, unknown> = {};
  let total = 0;

  for (const key of TOP_LEVEL_STRING_FIELDS) {
    const v = (report as unknown as Record<string, unknown>)[key];
    if (typeof v !== 'string') continue;
    const hits = findMetaInstructionsInText(key, v);
    if (hits.length === 0) continue;
    allMatches.push(...hits);
    const r = stripMetaInstructionsFromText(v);
    if (r.count > 0) {
      total += r.count;
      patch[key] = r.next;
    }
  }

  for (const key of NARRATIVE_OBJECT_FIELDS) {
    const v = (report as unknown as Record<string, unknown>)[key];
    collectMetaInstructionMatches(key, v, 0, allMatches);
    const r = stripMetaFromValue(v);
    if (r.count > 0) {
      total += r.count;
      patch[key] = r.next;
    }
  }

  for (const key of ['developmentPathway', 'recommendations', 'appendix'] as const) {
    const v = (report as unknown as Record<string, unknown>)[key];
    if (!Array.isArray(v)) continue;
    collectMetaInstructionMatches(key, v, 0, allMatches);
    const r = stripMetaFromValue(v);
    if (r.count > 0) {
      total += r.count;
      patch[key] = r.next;
    }
  }

  if (allMatches.length === 0 && total === 0) return report;

  console.info(
    `[mel:meta-instruction-leak] reportId=${report.id} count=${allMatches.length} stripped=${total}`,
  );

  const existingWarnings = Array.isArray(report.qualityWarnings) ? report.qualityWarnings : [];
  const newWarning = {
    type: 'meta-instruction-leak' as const,
    count: allMatches.length,
    samples: allMatches.slice(0, 3).map(m => ({
      sectionId: m.sectionId,
      family: m.family,
      // Truncate excerpts to 200 chars for telemetry hygiene (R7).
      excerpt: m.excerpt.slice(0, 200),
    })),
  };

  return {
    ...report,
    ...patch,
    qualityWarnings: [...existingWarnings, newWarning],
  } as SiteRagReport;
}

/* ─── RVG — Reviewer-voice quality gate (Tier-1 pre-demo hotfix) ─── */

/*
 * 'attribution-directive' — verification / review / sourcing directives.
 *
 * "<X> should|must|needs to|ought to be verified|reviewed|sourced" is BOTH the
 * QA critic's voice ("these figures should be verified before publication")
 * and the diligence wording the report prompts ASK for ("the 343 acre-foot
 * right must be verified with KDA-DWR"). The verbs alone are not evidence of
 * reviewer voice: until 2026-09-18 they were, and 23 gated sections across 17
 * stored reports were all that false positive.
 *
 * A directive is reviewer voice when EITHER
 *   (1) its tail names the REVIEW/PUBLICATION context, whatever the subject:
 *       "... before publication / release / sharing / inclusion", "... before
 *       the report is final|published", "... against the SED", "... against
 *       the cited sources", "... by the reviewer|editor|author|QA team"; OR
 *   (2) its SUBJECT is the report's own text or numbers ("these figures",
 *       "the claims made above", "unsupported statements", "this section")
 *       and its tail is not diligence: a verification ACTOR ("with the
 *       utility", "by qualified counsel", "with the county assessor", "with
 *       KDA-DWR") or a project-diligence CONTEXT ("during detailed design",
 *       "in the field", "through a hydrogeological study", "as part of due
 *       diligence", "before financial close").
 */

/** "should be verified", "must be independently reviewed", "needs to be sourced". */
const RVG_DIRECTIVE_RE = new RegExp(
  '\\b(?:should|must|needs?\\s+to|ought\\s+to)\\s+be\\s+'
  + '(?:(?:independently|further|properly|carefully|clearly|explicitly|thoroughly|re-?)\\s*)?'
  + '(verified|reviewed|sourced|fact-?checked)\\b',
  'gi',
);

/** Nouns that name the report's own TEXT: a critique subject with any
 *  determiner, or bare at the start of a sentence ("Figures should be ..."). */
const RVG_TEXT_NOUN =
  '(?:claims?|figures?|numbers?|statements?|assertions?|facts?|citations?|references?'
  + '|report|narrative|summary|section|draft|text|paragraphs?|content|table)';

/** Nouns that can equally name SITE quantities ("cost estimates should be
 *  verified" is diligence wording): a critique subject only with a pointing
 *  determiner ("these values", "all estimates") or a critique adjective
 *  ("unsupported numbers"), never with "the" or bare. */
const RVG_QUANTITY_NOUN =
  '(?:values?|estimates?|data(?:points?)?|sources?|assumptions?|calculations?|quantities|totals?|metrics?)';

const RVG_SUBJECT_POST_MODIFIER =
  '(?:\\s+(?:(?:made|used|quoted|given|shown|reported|cited|presented|provided|listed|stated)\\s+)?'
  + '(?:above|here|herein|below|in\\s+(?:this|the)\\s+(?:executive\\s+summary|report|section|draft|summary|table|analysis|narrative|paragraph))'
  + '|\\s+(?:cited|presented|provided|listed|stated|quoted|used))?';

const RVG_POINTING_DETERMINER =
  '\\b(?:these|those|this|that|all|every|each|such|any|its|their|the\\s+report\'s|this\\s+report\'s|the\\s+analysis\'s)\\s+';
const RVG_CRITIQUE_ADJECTIVE =
  '\\b(?:unsupported|unsourced|uncited|unverified|unattributed|unreferenced)\\s+';

/**
 * The critique SUBJECT, immediately before the directive: a determiner (or a
 * critique adjective, or a sentence start for text nouns), up to two
 * modifier words, the noun, and an optional post-modifier ("made above", "in
 * the executive summary").
 */
const RVG_CRITIQUE_SUBJECT_RE = new RegExp(
  '(?:'
  + `(?:(?:\\bthe\\s+|${RVG_POINTING_DETERMINER}|${RVG_CRITIQUE_ADJECTIVE}|(?:^|[.!?;:]\\s+))(?:[\\w-]+\\s+){0,2}?${RVG_TEXT_NOUN})`
  // "Analysis" alone names the report ("the analysis should be reviewed");
  // with a modifier it names SITE work ("the groundwater analysis").
  + `|(?:(?:\\bthe\\s+|${RVG_POINTING_DETERMINER}|(?:^|[.!?;:]\\s+))analysis)`
  + `|(?:(?:${RVG_POINTING_DETERMINER}|${RVG_CRITIQUE_ADJECTIVE})(?:[\\w-]+\\s+){0,2}?${RVG_QUANTITY_NOUN})`
  + `)${RVG_SUBJECT_POST_MODIFIER}\\s+$`,
  'i',
);

/** A bare "This"/"It" subject: the report's own statement. */
const RVG_BARE_PRONOUN_SUBJECT_RE = /(?:^|[.!?;:]\s+)(?:this|it)\s+$/i;

/** Tail context that makes ANY verification directive reviewer voice. */
const RVG_REVIEWER_TAIL_RE = new RegExp(
  '\\b(?:before|prior\\s+to|ahead\\s+of)\\s+(?:(?:its|their|the)\\s+)?'
  + '(?:publication|publishing|release|releasing|sharing|distribution|finali[sz]ation|inclusion|issuance|circulation|submission\\s+of\\s+(?:this|the)\\s+report)(?!\\s+of\\b)'
  + '|\\b(?:before|until)\\s+(?:this|it|the\\s+(?:report|analysis|draft|document|section|summary|narrative))\\s+(?:is|goes)\\s+'
  + '(?:final|finali[sz]ed|published|released|shared|issued|distributed|circulated|out)\\b'
  + '|\\b(?:against|with|via|in|to)\\s+the\\s+(?:SED|site\\s+evidence(?:\\s+document)?|cited\\s+sources?|source\\s+documents?|references?(?:\\s+list)?|citations?|bibliography)\\b'
  + '|\\bby\\s+(?:(?:a|an|the|our|your)\\s+)?(?:senior\\s+)?(?:reviewers?|editors?|editorial\\s+(?:team|board|staff)|authors?|writers?|analysts?|QA(?:\\s+(?:team|department|reviewers?))?|quality\\s+(?:assurance|team|reviewers?))\\b'
  + '|\\bwith\\s+(?:the|an?)\\s+(?:reviewers?|editors?|authors?|writers?)\\b',
  'i',
);

/**
 * A verification ACTOR right after the directive turns a critique-subject
 * sentence back into reader-directed diligence. "by|with|from", an optional
 * article, up to four modifier words (never a review/QA word), then an actor
 * noun; or an agency acronym ("with KDA-DWR", "with ERCOT").
 */
const RVG_ACTOR_EXEMPTION_RE = new RegExp(
  '^\\s*(?:by|with|from|through)\\s+(?:(?:a|an|the|its|their|each|any|your|qualified|licensed|independent)\\s+)*'
  + '(?:(?!(?:qa|quality|editorial|review(?:ing)?|reviewers?|editors?|publication|publishing|copy)\\b)[\\w\'’.-]+\\s+){0,4}'
  + '(?:counsel|attorneys?|lawyers?|engineers?|hydrologists?|geologists?|hydrogeologists?|surveyors?|consultants?|professionals?|experts?'
  + '|authorit(?:y|ies)|agenc(?:y|ies)|regulators?|utilit(?:y|ies)|departments?|divisions?|commissions?|offices?|boards?|districts?'
  + '|operators?|providers?|assessors?|appraisers?|planners?|clerks?|recorders?|registr(?:y|ies|ars?)|landowners?|owners?|sellers?'
  + '|lenders?|insurers?|underwriters?|inspectors?|officials?|staff|county|city|municipality|state|states|ISO|RTO|TSP|DSP|co-?op'
  + '|cooperatives?|interconnection\\s+customers?|transmission\\s+providers?)\\b',
  'i',
);
/** Agency acronym actor (case-sensitive): "with KDA-DWR", "by ERCOT". */
const RVG_ACRONYM_ACTOR_RE = /^\s*(?:by|with|from)\s+(?:the\s+)?[A-Z][A-Z0-9]{1,}(?:[-/&][A-Z0-9]+)*\b/;

/** Project-diligence context right after the directive. */
const RVG_DILIGENCE_CONTEXT_RE = new RegExp(
  '^\\s*(?:'
  + '(?:during|in)\\s+(?:the\\s+)?(?:detailed\\s+|final\\s+|front-end\\s+)?(?:design|engineering|diligence|due\\s+diligence|permitting|feasibility|FEED|interconnection\\s+stud(?:y|ies)|site\\s+investigation)'
  + '|in\\s+the\\s+field|on[- ]site|onsite'
  + '|through\\s+|via\\s+(?!the\\s+(?:SED|site\\s+evidence))'
  + '|as\\s+part\\s+of\\s+'
  + '|(?:before|prior\\s+to|ahead\\s+of|at|by)\\s+(?:the\\s+)?(?:financial\\s+close|closing|close|construction|energi[sz]ation|interconnection|acquisition|purchase|site\\s+control|notice\\s+to\\s+proceed|NTP|FID|final\\s+investment\\s+decision|commissioning|contract\\s+execution|execution|lease\\s+execution|design\\s+freeze|procurement|groundbreaking)'
  + '|once\\s+|when\\s+|after\\s+|against\\s+(?:the\\s+)?(?:[\\w-]+\\s+){0,3}(?:tariffs?|filings?|records?|permits?|surveys?|stud(?:y|ies)|maps?|registers?|databases?|rulings?|orders?|schedules?|agreements?|contracts?|deeds?|titles?|plats?|applications?)\\b'
  + ')',
  'i',
);

function sentenceBounds(text: string, index: number): { start: number; end: number } {
  let start = 0;
  for (let i = index - 1; i >= 0; i -= 1) {
    const ch = text[i]!;
    if (ch === '\n' || ((ch === '.' || ch === '!' || ch === '?') && /\s/.test(text[i + 1] ?? ''))) {
      start = i + 1;
      break;
    }
  }
  let end = text.length;
  for (let i = index; i < text.length; i += 1) {
    const ch = text[i]!;
    if (ch === '\n' || ((ch === '.' || ch === '!' || ch === '?') && (i + 1 >= text.length || /\s/.test(text[i + 1]!)))) {
      end = i;
      break;
    }
  }
  return { start, end };
}

/** True when `text` carries a verification/review/sourcing directive in reviewer voice. */
function hasReviewerVerificationDirective(text: string): boolean {
  const re = new RegExp(RVG_DIRECTIVE_RE.source, RVG_DIRECTIVE_RE.flags);
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    const { start, end } = sentenceBounds(text, match.index);
    const tail = text.slice(match.index + match[0].length, end);
    if (RVG_REVIEWER_TAIL_RE.test(tail)) return true;
    // The subject is judged on the sentence text before the directive; a
    // leading sentence boundary is kept so a bare plural at the start of a
    // sentence ("Figures should be verified.") still anchors.
    const head = `. ${text.slice(start, match.index).trimStart()}`;
    const critiqueSubject = RVG_CRITIQUE_SUBJECT_RE.test(head) || RVG_BARE_PRONOUN_SUBJECT_RE.test(head);
    if (!critiqueSubject) continue;
    const diligence = RVG_ACTOR_EXEMPTION_RE.test(tail)
      || RVG_ACRONYM_ACTOR_RE.test(tail)
      || RVG_DILIGENCE_CONTEXT_RE.test(tail);
    if (!diligence) return true;
  }
  return false;
}

interface ReviewerVoicePattern {
  family: string;
  matches(text: string): boolean;
}

const fromRegex = (regex: RegExp, family: string): ReviewerVoicePattern => ({
  family,
  matches: (text) => new RegExp(regex.source, regex.flags).test(text),
});

/**
 * Phrases that indicate QA reviewer feedback leaked into published
 * narrative (Holly Ridge Test Fit executiveSummary, 2026-05-26).
 * Distinct from MEL meta-instructions — these read as critique of the
 * draft ("the report only contains…", "should be attributed") rather
 * than self-directed framing directives.
 */
const REVIEWER_VOICE_PATTERNS: ReadonlyArray<ReviewerVoicePattern> = [
  fromRegex(/\b(?:the report|this report|the analysis)\s+(?:only|merely|simply)\s+(?:contains|provides|offers|includes)\b/i, 'report-critique'),
  // Attribution/format directives are reviewer voice with ANY subject.
  fromRegex(/\b(?:should be|must be|needs to be|ought to be)\s+(?:attributed|cited|formatted)\b/i, 'attribution-directive'),
  // Verification/review/sourcing directives: reviewer context or critique subject.
  { family: 'attribution-directive', matches: hasReviewerVerificationDirective },
  fromRegex(/\bimproperly\s+(?:formatted|cited|sourced|attributed|structured)\b/i, 'format-critique'),
  fromRegex(/\b(?:all factual claims|every claim|every datapoint)\s+(?:should|must|need to|ought to)\b/i, 'claims-directive'),
  fromRegex(/\bplaceholder\s+citation\b/i, 'placeholder-citation'),
  fromRegex(/\[\s*ref\s+[a-z_-]+\s*\]/i, 'named-ref-placeholder'),
];

export interface ReviewerVoiceLeakReport {
  leaked: boolean;
  matchedPatterns: string[];
}

/**
 * Detect reviewer-feedback prose that must never ship as narrative.
 * Pure / no side effects.
 */
export function detectReviewerVoiceLeak(text: string): ReviewerVoiceLeakReport {
  if (!text || typeof text !== 'string') return { leaked: false, matchedPatterns: [] };
  const matched: string[] = [];
  for (const { family, matches } of REVIEWER_VOICE_PATTERNS) {
    if (!matched.includes(family) && matches(text)) matched.push(family);
  }
  return { leaked: matched.length > 0, matchedPatterns: matched };
}

/**
 * Top-level ARRAY surfaces the gate inspects (beside the two field lists).
 * `executiveSummaryCriticalRisks`: the executive summary's risk rows, whose
 * risk text `conclusion.topRisks` copies verbatim (buildSynthesisConclusion).
 * The gate always inspected the copy; until the 2026-09-18 owner decision a
 * withheld copy blocked export, so the uninspected original never shipped.
 * Now it would print the withheld wording, reviewer voice included, in the
 * Critical Risks section of every export and the public edition.
 */
const RVG_ARRAY_FIELDS = ['developmentPathway', 'recommendations', 'appendix', 'executiveSummaryCriticalRisks'] as const;

/**
 * Top-level strings ONLY this gate inspects, beside TOP_LEVEL_STRING_FIELDS
 * (which the verifier-tag, dash and MEL passes share, so they stay as they
 * are). The executive summary persists its BTM viability text three times:
 * these two fields and `conclusion.btmDecisionSummary`. The gate always
 * inspected the conclusion copy; the same reasoning as
 * `executiveSummaryCriticalRisks` above applies to these two (the BTM
 * Viability section and the conclusion fallbacks print them).
 */
const RVG_EXTRA_STRING_FIELDS = ['btmViabilityAssessment', 'btmDecisionSummary'] as const;

/** Every top-level string the gate inspects. */
const RVG_STRING_FIELDS = [...TOP_LEVEL_STRING_FIELDS, ...RVG_EXTRA_STRING_FIELDS] as const;

interface ReviewerVoiceHit {
  segments: ReportLeafPathSegment[];
  matchedPatterns: string[];
  excerpt: string;
  /** The WHOLE withheld text. Never persisted: only the gate's own sweeps
   *  read it, to find what was lifted or copied from this leaf (claims, the
   *  overview's copy, quoted spans in findings, issues and guard samples). */
  original: string;
}

/**
 * Walk one gated surface and WITHHOLD (null) every string leaf that carries
 * reviewer voice, recording its exact path. Same depth bounds as the
 * pre-2026-09-18 walker (which leaves are inspected is unchanged; only what
 * happens on a hit changed). Reference-preserving: an untouched subtree is
 * returned as-is.
 */
function withholdReviewerVoiceLeaves(
  value: unknown,
  segments: ReportLeafPathSegment[],
  depth: number,
  hits: ReviewerVoiceHit[],
): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') {
    const det = detectReviewerVoiceLeak(value);
    if (!det.leaked) return value;
    hits.push({ segments, matchedPatterns: det.matchedPatterns, excerpt: value.slice(0, 200), original: value });
    return null;
  }
  if (Array.isArray(value)) {
    if (depth >= 3) return value;
    let mutated = false;
    const next = value.map((entry, index) => {
      const r = withholdReviewerVoiceLeaves(entry, [...segments, index], depth + 1, hits);
      if (r !== entry) mutated = true;
      return r;
    });
    return mutated ? next : value;
  }
  if (typeof value === 'object') {
    if (depth >= 3) return value;
    let mutated = false;
    const next: Record<string, unknown> = { ...(value as Record<string, unknown>) };
    for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
      const r = withholdReviewerVoiceLeaves(entry, [...segments, key], depth + 1, hits);
      if (r !== entry) {
        mutated = true;
        next[key] = r;
      }
    }
    return mutated ? next : value;
  }
  return value;
}

/**
 * Wording-only form of a text, space-padded so `includes` matches whole
 * words: case, bracketed tokens (`[Ref 3]`, `[verified sed-section:grid]`),
 * dashes and punctuation are dropped. Claims are harvested before the
 * verifier-tag and dash passes rewrite the prose, so a claim sentence and its
 * leaf only agree once both are reduced to their words. '' when no words.
 */
function claimMatchText(text: string): string {
  const words = text
    .toLowerCase()
    .replace(/\[[^\]]*\]/g, ' ')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
  return words ? ` ${words} ` : '';
}

type ReportClaimRow = NonNullable<SiteRagReport['claims']>[number];

/**
 * RVG × claims — drop every claim lifted from a withheld leaf.
 *
 * `report.claims[]` is harvested from the narrative BEFORE this gate runs (CT6
 * per-sentence claims come back with the narrative; CSE structured claims are
 * extracted from it), and the frozen document, the complete-record appendix of
 * every export, the CSV and the public share all print it. Withholding the leaf
 * without its claims would ship the unreviewed sentences next to the
 * "not assessed" notice. A claim belongs to a withheld leaf when:
 *   - its section id names a withheld top-level field (`executiveSummary`), or
 *   - its sentence occurs in a withheld leaf's text, or
 *   - it is a structured claim (no sentence) of the leaf's container
 *     (`waterAnalysis`) whose value occurs in the withheld text. The value may
 *     also sit in a sibling leaf; dropping it then loses one structured claim,
 *     which is the safe direction.
 */
function claimsOutsideWithheldLeaves(
  claims: readonly ReportClaimRow[],
  hits: readonly ReviewerVoiceHit[],
): ReportClaimRow[] {
  const wholeFields = new Set(
    hits.filter((hit) => hit.segments.length === 1).map((hit) => String(hit.segments[0])),
  );
  const leaves = hits
    .map((hit) => ({ container: String(hit.segments[0]), words: claimMatchText(hit.original) }))
    .filter((leaf) => leaf.words.length > 0);
  const fromWithheldLeaf = (claim: ReportClaimRow): boolean => {
    const ids = [claim.sectionId, claim.reportSectionId]
      .filter((id): id is string => typeof id === 'string')
      .map((id) => id.trim());
    if (ids.some((id) => wholeFields.has(id))) return true;
    const sentence = typeof claim.text === 'string' ? claimMatchText(claim.text) : '';
    if (sentence) return leaves.some((leaf) => leaf.words.includes(sentence));
    const raw = claim.value;
    if (typeof raw !== 'string' && typeof raw !== 'number' && typeof raw !== 'boolean') return false;
    const value = claimMatchText(String(raw));
    if (!value) return false;
    return leaves.some((leaf) => ids.includes(leaf.container) && leaf.words.includes(value));
  };
  return claims.filter((claim) => !(claim && typeof claim === 'object' && fromWithheldLeaf(claim)));
}

/**
 * The `cse-internal-conflict` record lists the claim VALUES that disagree, so
 * a value only a dropped claim carried must leave it too. Each entry keeps the
 * values some surviving claim of the same (section, field) still asserts, and
 * stays only while at least two still disagree (the record's own invariant).
 * Normalisation mirrors `detectClaimConflicts` (claimExtraction.ts).
 */
function conflictWarningsForClaims(
  warnings: NonNullable<SiteRagReport['qualityWarnings']>,
  claims: readonly ReportClaimRow[],
): NonNullable<SiteRagReport['qualityWarnings']> {
  const asserted = new Map<string, Set<string>>();
  for (const claim of claims) {
    const section = typeof claim.reportSectionId === 'string' ? claim.reportSectionId.trim() : '';
    const field = typeof claim.field === 'string' ? claim.field.trim() : '';
    const value = claim.value == null ? '' : String(claim.value).trim();
    if (!section || !field || !value) continue;
    const key = `${section}:::${field}`;
    const values = asserted.get(key) ?? new Set<string>();
    values.add(value);
    asserted.set(key, values);
  }
  const out: NonNullable<SiteRagReport['qualityWarnings']> = [];
  for (const warning of warnings) {
    if (warning?.type !== 'cse-internal-conflict') {
      out.push(warning);
      continue;
    }
    const recorded: ReadonlyArray<(typeof warning.entries)[number]> = Array.isArray(warning.entries) ? warning.entries : [];
    const entries = recorded
      .map((entry) => ({
        ...entry,
        values: entry.values.filter((value) => asserted.get(`${entry.reportSectionId}:::${entry.field}`)?.has(value)),
      }))
      .filter((entry) => entry.values.length >= 2);
    if (entries.length > 0) out.push({ ...warning, count: entries.length, entries });
  }
  return out;
}

/** A reproduced span is a run of at least this many consecutive words of a
 *  withheld leaf, or the whole leaf when it is shorter. */
const WITHHELD_RUN_MIN_WORDS = 8;
/** A leaf shorter than this is never matched on its own: a two-word run
 *  ("placeholder citation") is a phrase, not a reproduction. */
const WITHHELD_LEAF_MIN_WORDS = 3;
/** "…", “…”, ‘…’, and '…' when its marks are not apostrophes. */
const QUOTATION_RE = /"[^"\n]*"|“[^”\n]*”|‘[^’\n]*’|(?<![\p{L}\p{N}])'[^'\n]*'(?![\p{L}\p{N}])/gu;

interface WordToken {
  word: string;
  start: number;
  end: number;
}

/**
 * The words of a text with their offsets, on the terms `claimMatchText` uses:
 * lower-cased, bracketed tokens skipped (`[Ref 3]`, a verifier tag, a
 * `[withheld narrative]` marker already in place), dashes and punctuation
 * dropped. A finding written before the verifier-tag and dash passes still
 * lines up with the leaf it quotes.
 */
function wordTokens(text: string): WordToken[] {
  const masked = text.replace(/\[[^\]]*\]/g, (token) => ' '.repeat(token.length));
  const tokens: WordToken[] = [];
  for (const match of masked.matchAll(/[\p{L}\p{N}]+/gu)) {
    const start = match.index ?? 0;
    tokens.push({ word: match[0].toLowerCase(), start, end: start + match[0].length });
  }
  return tokens;
}

/**
 * Offsets of every span of `text` that reproduces a withheld leaf:
 *   - a run of >= WITHHELD_RUN_MIN_WORDS consecutive words of the leaf (the
 *     whole leaf when it is shorter), counting a word cut off at either edge
 *     (the sanity prompt quotes the summary's first 200 characters, so the
 *     echo ends mid-word), which goes with the run;
 *   - the whole quotation such a run sits in, marks included.
 * A short quotation on its own is NOT a reproduction: a finding that names
 * '1803 Electric Cooperative' quotes an entity other sections also name, and
 * hiding it would hide the finding (Redhead sanityFindings[12]).
 * Spans with only spaces or punctuation between them are merged.
 */
function withheldSpans(text: string, leaves: readonly (readonly string[])[]): Array<[number, number]> {
  const tokens = wordTokens(text);
  if (tokens.length === 0) return [];
  const words = tokens.map((token) => token.word);
  const spans: Array<[number, number]> = [];
  for (const leaf of leaves) {
    const minRun = Math.min(WITHHELD_RUN_MIN_WORDS, leaf.length);
    if (minRun < WITHHELD_LEAF_MIN_WORDS) continue;
    const positions = new Map<string, number[]>();
    leaf.forEach((word, j) => {
      const at = positions.get(word);
      if (at) at.push(j);
      else positions.set(word, [j]);
    });
    let i = 0;
    while (i < words.length) {
      let run = 0;
      let from = -1;
      for (const j of positions.get(words[i]!) ?? []) {
        let k = 0;
        while (i + k < words.length && j + k < leaf.length && words[i + k] === leaf[j + k]) k += 1;
        if (k > run) {
          run = k;
          from = j;
        }
      }
      let first = i;
      let last = i + run - 1;
      if (run > 0) {
        if (first > 0 && from > 0 && leaf[from - 1]!.endsWith(words[first - 1]!)) first -= 1;
        if (last + 1 < words.length && from + run < leaf.length && leaf[from + run]!.startsWith(words[last + 1]!)) last += 1;
      }
      if (run === 0 || last - first + 1 < minRun) {
        i += 1;
        continue;
      }
      spans.push([tokens[first]!.start, tokens[last]!.end]);
      i = last + 1;
    }
  }
  if (spans.length === 0) return [];
  for (const match of text.matchAll(QUOTATION_RE)) {
    const start = match.index ?? 0;
    const end = start + match[0].length;
    if (spans.some(([s, e]) => s < end && e > start)) spans.push([start, end]);
  }
  spans.sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [];
  for (const span of spans) {
    const previous = merged[merged.length - 1];
    if (previous && (span[0] <= previous[1] || !/[\p{L}\p{N}]/u.test(text.slice(previous[1], span[0])))) {
      previous[1] = Math.max(previous[1], span[1]);
    } else {
      merged.push([span[0], span[1]]);
    }
  }
  return merged;
}

/** `text` with every span that reproduces a withheld leaf replaced by the
 *  marker; the same string when nothing matched. */
function redactWithheldText(text: string, leaves: readonly (readonly string[])[]): string {
  const spans = withheldSpans(text, leaves);
  if (spans.length === 0) return text;
  let out = '';
  let cursor = 0;
  for (const [start, end] of spans) {
    out += text.slice(cursor, start) + WITHHELD_NARRATIVE_SPAN_MARKER;
    cursor = end;
  }
  return out + text.slice(cursor);
}

/** The words of one withheld leaf (`wordTokens` terms) and the top-level
 *  field it sits in. Never persisted. */
interface WithheldLeafWords {
  container: string;
  words: string[];
}

function withheldLeafWords(hits: readonly ReviewerVoiceHit[]): WithheldLeafWords[] {
  return hits.map((hit) => ({
    container: String(hit.segments[0]),
    words: wordTokens(hit.original).map((token) => token.word),
  }));
}

/** True when `words` occur in `leaf`, in order and adjacent. */
function wordsWithin(words: readonly string[], leaf: readonly string[]): boolean {
  if (words.length === 0 || words.length > leaf.length) return false;
  for (let i = 0; i + words.length <= leaf.length; i += 1) {
    let k = 0;
    while (k < words.length && leaf[i + k] === words[k]) k += 1;
    if (k === words.length) return true;
  }
  return false;
}

/**
 * True when `text` reproduces a withheld leaf: it holds a span
 * `withheldSpans` finds, or ALL of its words (at least
 * WITHHELD_LEAF_MIN_WORDS) sit adjacent inside one leaf. The second arm
 * catches a copy shorter than a run ("Redhead is a strong candidate for
 * development." is 7 words).
 */
function copiesWithheldLeaf(text: string, leaves: readonly WithheldLeafWords[]): boolean {
  if (withheldSpans(text, leaves.map((leaf) => leaf.words)).length > 0) return true;
  const words = wordTokens(text).map((token) => token.word);
  return words.length >= WITHHELD_LEAF_MIN_WORDS && leaves.some((leaf) => wordsWithin(words, leaf.words));
}

/**
 * RVG × project-intent overview — drop every paragraph that copies a
 * withheld leaf.
 *
 * Assembly stamps `projectIntentOverview` (buildIntentOverviewStamp) BEFORE
 * this gate runs, and its last paragraph is the executive summary's first
 * paragraph, word for word. When the reviewer voice sits in a LATER
 * paragraph of the executive summary, the gate withholds the summary but
 * the overview (clean on its own) keeps that copy. Render and export used to
 * drop it as a duplicate of `executiveSummary` (cleanupOverviewProse), which
 * is null once withheld, so the copy printed directly under the "not
 * assessed" notice in every export, the frozen document and the public
 * edition. The whole paragraph goes, not a marker: it is only a copy, and
 * the intent paragraphs around it stay. `null` when no paragraph is left
 * (render then rebuilds the intent overview without a summary snippet).
 */
function overviewWithoutWithheldCopies(
  overview: string,
  leaves: readonly WithheldLeafWords[],
): string | null {
  const paragraphs = overview.split(/\n[ \t]*\n\s*/);
  const kept = paragraphs.filter((paragraph) => !copiesWithheldLeaf(paragraph, leaves));
  if (kept.length === paragraphs.length) return overview;
  const joined = kept.map((paragraph) => paragraph.trim()).filter(Boolean).join('\n\n');
  return joined || null;
}

type ReviewIssueRow = NonNullable<NonNullable<SiteRagReport['qualityReview']>['issues']>[number];

/**
 * RVG × reviewer issues — keep every issue, drop the withheld wording.
 *
 * `qualityReview.issues` is written from the reviewer's verdict BEFORE this
 * gate, and an issue quotes the text it criticises ("Uncited figure: "<the
 * sentence>" carries no [Ref N]"). The frozen document freezes each one as
 * a quality-decision finding, which the public edition, the PDF appendix,
 * DOCX, PPTX and CSV all print. Each span that reproduces a withheld leaf
 * becomes WITHHELD_NARRATIVE_SPAN_MARKER; section, verdict and counts stay.
 * The array is returned by identity when nothing was redacted.
 */
function reviewIssuesWithoutWithheldText(
  issues: readonly ReviewIssueRow[],
  leaves: readonly WithheldLeafWords[],
): { issues: ReviewIssueRow[]; redacted: number } {
  const words = leaves.map((leaf) => leaf.words);
  let redacted = 0;
  const next = issues.map((issue) => {
    if (!issue || typeof issue !== 'object' || typeof issue.issue !== 'string') return issue;
    const text = redactWithheldText(issue.issue, words);
    if (text === issue.issue) return issue;
    redacted += 1;
    return { ...issue, issue: text };
  });
  return { issues: redacted > 0 ? next : (issues as ReviewIssueRow[]), redacted };
}

/** Quality warnings whose samples quote the prose (`phrase`), written by the
 *  RNH-2 and PSE-3 guards BEFORE this gate. */
const PHRASE_SAMPLE_WARNING_TYPES: ReadonlySet<string> = new Set(['rnh-stance-mismatch', 'pse-sentiment-conflict']);

/**
 * True when a guard's sample phrase was cut from a withheld leaf: it holds a
 * reproduced span (a PSE phrase capped at 120 characters ends "...", after a
 * word the cap may have cut; the span's edge rule counts that word), or its
 * words all sit inside a withheld leaf of the same top-level field (the
 * sample's `sectionId`), however short.
 */
function samplePhraseCopiesWithheldLeaf(
  phrase: string,
  sectionId: unknown,
  leaves: readonly WithheldLeafWords[],
): boolean {
  if (withheldSpans(phrase, leaves.map((leaf) => leaf.words)).length > 0) return true;
  const words = wordTokens(phrase).map((token) => token.word);
  return leaves.some((leaf) => leaf.container === sectionId && wordsWithin(words, leaf.words));
}

type QualityWarningRow = NonNullable<SiteRagReport['qualityWarnings']>[number];

/**
 * RVG × stance / sentiment guard samples — keep the warning, drop a phrase
 * cut from a withheld leaf.
 *
 * RNH-2 and PSE-3 record the matched prose as each sample's `phrase` before
 * this gate runs (on Redhead, 17 of a withheld recommendation's 19 words),
 * and the DOCX/PPTX quality appendix ("Examples: …") and the CSV print it.
 * Such a phrase becomes WITHHELD_NARRATIVE_SPAN_MARKER; type, count, stance,
 * tech, score and section stay, so no warning is hidden. The array is
 * returned by identity when nothing was redacted.
 */
function phraseSamplesWithoutWithheldText(
  warnings: readonly QualityWarningRow[],
  leaves: readonly WithheldLeafWords[],
): { warnings: QualityWarningRow[]; redacted: number } {
  let redacted = 0;
  const next = warnings.map((warning) => {
    if (!warning || !PHRASE_SAMPLE_WARNING_TYPES.has(warning.type)) return warning;
    const samples = (warning as { samples?: unknown }).samples;
    if (!Array.isArray(samples)) return warning;
    let changed = false;
    const nextSamples = samples.map((sample: unknown) => {
      if (!sample || typeof sample !== 'object') return sample;
      const row = sample as { sectionId?: unknown; phrase?: unknown };
      if (typeof row.phrase !== 'string' || row.phrase === WITHHELD_NARRATIVE_SPAN_MARKER) return sample;
      if (!samplePhraseCopiesWithheldLeaf(row.phrase, row.sectionId, leaves)) return sample;
      changed = true;
      return { ...row, phrase: WITHHELD_NARRATIVE_SPAN_MARKER };
    });
    if (!changed) return warning;
    redacted += 1;
    return { ...warning, samples: nextSamples } as QualityWarningRow;
  });
  return { warnings: redacted > 0 ? next : (warnings as QualityWarningRow[]), redacted };
}

type SanityFindingRow = NonNullable<SiteRagReport['sanityFindings']>[number];

/** Finding fields that identify or classify it; every OTHER string field is
 *  text the model or the user wrote, and is redacted. */
const SANITY_FINDING_KEY_FIELDS: ReadonlySet<string> = new Set([
  'id', 'category', 'section', 'severity', 'verificationStatus', 'resolution', 'resolvedAt',
]);

/** Verified-category findings (the reader card offers them no "Apply on
 *  rerun"): a confirmation says the quoted claim is right. */
const SANITY_CONFIRMATION_CATEGORIES: ReadonlySet<string> = new Set(['strength-verified', 'risk-verified']);

/**
 * RVG × sanity findings — keep every finding, drop the withheld wording.
 *
 * The final sanity check runs BEFORE this gate, and its prompt quotes
 * "SUMMARY CLAIM: <first 200 characters of executiveSummary>" (and other
 * leaves), which the model echoes into `finding` (Redhead fixture,
 * sanityFindings[7]). The DOCX/PPTX quality appendix and the CSV print these
 * findings. Each span that reproduces a withheld leaf (`withheldSpans`)
 * becomes WITHHELD_NARRATIVE_SPAN_MARKER. The finding itself (id, category,
 * section, severity, status and every other word) stays: dropping a quality
 * finding would hide quality information, which is the unsafe direction.
 *
 * One exception to "every other word": a CONFIRMATION of a withheld claim
 * (verified category, status 'confirmed', severity not high) whose quoted
 * subject (`finding`) was redacted. Its verdict ("CONFIRMED. … a fair and
 * accurate overview") endorses a narrative the report says was not assessed,
 * and its evidence restates that narrative. Its suggestion becomes the
 * withheld-narrative notice, its evidence the marker, and its status
 * 'not-applicable' (no verification applies to a withheld claim). Nothing
 * is hidden: a confirmation reports no defect. Unverified, contradicted,
 * partial and high-severity findings keep their words.
 * The array is returned by identity when nothing was redacted.
 */
function sanityFindingsWithoutWithheldText(
  findings: readonly SanityFindingRow[],
  leafWords: readonly WithheldLeafWords[],
): { findings: SanityFindingRow[]; redacted: number } {
  const leaves = leafWords.map((leaf) => leaf.words);
  const redactFields = <T extends object>(row: T, keep: ReadonlySet<string>): T => {
    let next: Record<string, unknown> | null = null;
    for (const [key, value] of Object.entries(row)) {
      if (typeof value !== 'string' || keep.has(key)) continue;
      const redacted = redactWithheldText(value, leaves);
      if (redacted === value) continue;
      next ??= { ...(row as Record<string, unknown>) };
      next[key] = redacted;
    }
    return next ? (next as T) : row;
  };
  const isConfirmation = (finding: SanityFindingRow): boolean => (
    SANITY_CONFIRMATION_CATEGORIES.has(finding.category)
    && finding.verificationStatus === 'confirmed'
    && finding.severity !== 'high'
  );
  let redacted = 0;
  const next = findings.map((finding) => {
    if (!finding || typeof finding !== 'object' || leaves.length === 0) return finding;
    let row = redactFields(finding, SANITY_FINDING_KEY_FIELDS);
    const directive = finding.applyDirective;
    if (directive && typeof directive === 'object') {
      const redactedDirective = redactFields(directive, new Set(['section']));
      if (redactedDirective !== directive) row = { ...row, applyDirective: redactedDirective };
    }
    if (row.finding !== finding.finding && isConfirmation(finding)) {
      row = {
        ...row,
        suggestion: WITHHELD_NARRATIVE_NOTICE,
        verificationStatus: 'not-applicable',
        ...(typeof row.evidence === 'string' ? { evidence: WITHHELD_NARRATIVE_SPAN_MARKER } : {}),
      };
    }
    if (row !== finding) redacted += 1;
    return row;
  });
  return { findings: redacted > 0 ? next : (findings as SanityFindingRow[]), redacted };
}

/**
 * RVG — WITHHOLD every narrative leaf containing reviewer-voice leaks before
 * persistence, and record exactly which leaf it was.
 *
 * Since 2026-09-18 the gate never persists a display string. It used to
 * replace the leaf with "<Container> unavailable — content failed quality
 * gate. Regenerate report." (labelled by the CONTAINER key, so a gated
 * `waterAnalysis.waterRightsAnalysis` read "Water Analysis unavailable"), and
 * that ops sentinel shipped to customers wherever the render scrub did not
 * reach. Now:
 *   - the WHOLE leaf is withheld (null). Dropping just the matched sentence
 *     is deliberately NOT done: that sentence is often the diligence caveat
 *     itself, and the remaining prose was never reviewed without it. The
 *     top-level `summary`
 *     withholds to '' because a string `summary` is the body-loaded signal
 *     across delivery and export gates.
 *   - `qualityWarnings` gains a `reviewer-voice-leak` record whose `withheld`
 *     rows carry each leaf's path and a label derived from the LEAF.
 *   - the leaf's claims are withheld with it: `claims[]` was harvested from
 *     the prose before this gate, and every export and the public share print
 *     it (`claimsOutsideWithheldLeaves`).
 *   - the sanity findings, written before this gate too, keep every finding
 *     but lose any span that reproduces the leaf's wording; the span reads
 *     WITHHELD_NARRATIVE_SPAN_MARKER (`sanityFindingsWithoutWithheldText`).
 *     A confirmation of the withheld claim also loses its verdict.
 *   - the other copies written before this gate go too, since export no
 *     longer stops them (review of 2026-09-18): the project-intent
 *     overview's paragraphs that copy the leaf
 *     (`overviewWithoutWithheldCopies`), the reviewer issues' quotes
 *     (`reviewIssuesWithoutWithheldText`) and the RNH-2 / PSE-3 sample
 *     phrases (`phraseSamplesWithoutWithheldText`). The CSV projects the
 *     assembly-time `qaFindings` receipts to non-text fields
 *     (reportExport.ts); the stored receipts are audit records and stay.
 *   - that record holds the report for revision (`resolveQualityHold`);
 *     since the 2026-09-18 owner decision it does not block export or
 *     publishing. Render and every export show an honest "not assessed"
 *     notice at the path. No regeneration call is made.
 *
 * Runs AFTER `stripMetaInstructions`. Pure; identity on clean reports.
 */
export function applyReviewerVoiceQualityGate(report: SiteRagReport): SiteRagReport {
  if (!report) return report;

  const patch: Record<string, unknown> = {};
  const hits: ReviewerVoiceHit[] = [];

  for (const key of RVG_STRING_FIELDS) {
    const v = (report as unknown as Record<string, unknown>)[key];
    if (typeof v !== 'string') continue;
    const det = detectReviewerVoiceLeak(v);
    if (!det.leaked) continue;
    patch[key] = key === 'summary' ? '' : null;
    hits.push({ segments: [key], matchedPatterns: det.matchedPatterns, excerpt: v.slice(0, 200), original: v });
  }

  for (const key of NARRATIVE_OBJECT_FIELDS) {
    const v = (report as unknown as Record<string, unknown>)[key];
    const next = withholdReviewerVoiceLeaves(v, [key], 0, hits);
    if (next !== v) patch[key] = next;
  }

  for (const key of RVG_ARRAY_FIELDS) {
    const v = (report as unknown as Record<string, unknown>)[key];
    if (!Array.isArray(v)) continue;
    const next = withholdReviewerVoiceLeaves(v, [key], 0, hits);
    if (next !== v) patch[key] = next;
  }

  if (hits.length === 0) return report;

  const withheld: WithheldNarrativeLeaf[] = hits.map((hit) => ({
    path: formatReportLeafPath(hit.segments),
    label: reportLeafLabel(hit.segments),
    reason: 'reviewer-voice-leak',
    matchedPatterns: [...new Set(hit.matchedPatterns)],
    excerpt: hit.excerpt.slice(0, 200),
  }));
  for (const leaf of withheld) {
    console.warn(
      `[rvg:reviewer-voice-leak] reportId=${report.id} withheld=${leaf.path} patterns=${leaf.matchedPatterns.join(',')}`,
    );
  }

  // The withheld leaves' claims go with them (see claimsOutsideWithheldLeaves).
  const priorClaims = Array.isArray(report.claims) ? report.claims : [];
  const claims = claimsOutsideWithheldLeaves(priorClaims, hits);
  const claimsDropped = claims.length !== priorClaims.length;
  if (claimsDropped) {
    console.warn(
      `[rvg:reviewer-voice-leak] reportId=${report.id} droppedClaims=${priorClaims.length - claims.length}`,
    );
  }

  const leaves = withheldLeafWords(hits);

  // The overview's copy of a withheld leaf goes with it (see
  // overviewWithoutWithheldCopies). A withheld overview is already null.
  const overview = Object.prototype.hasOwnProperty.call(patch, 'projectIntentOverview')
    ? patch.projectIntentOverview
    : report.projectIntentOverview;
  if (typeof overview === 'string' && overview.trim()) {
    const kept = overviewWithoutWithheldCopies(overview, leaves);
    if (kept !== overview) {
      patch.projectIntentOverview = kept;
      console.warn(
        `[rvg:reviewer-voice-leak] reportId=${report.id} droppedOverviewCopy=${kept === null ? 'all' : 'paragraphs'}`,
      );
    }
  }

  // The sanity findings keep their place, not the withheld wording they
  // quote (see sanityFindingsWithoutWithheldText).
  const priorFindings = report.sanityFindings;
  const findings = Array.isArray(priorFindings) ? sanityFindingsWithoutWithheldText(priorFindings, leaves) : null;
  if (findings && findings.redacted > 0) {
    console.warn(
      `[rvg:reviewer-voice-leak] reportId=${report.id} redactedSanityFindings=${findings.redacted}`,
    );
  }

  // So do the reviewer's issues (see reviewIssuesWithoutWithheldText).
  const priorReview = report.qualityReview;
  const issues = priorReview && Array.isArray(priorReview.issues)
    ? reviewIssuesWithoutWithheldText(priorReview.issues, leaves)
    : null;
  if (issues && issues.redacted > 0) {
    console.warn(
      `[rvg:reviewer-voice-leak] reportId=${report.id} redactedReviewIssues=${issues.redacted}`,
    );
  }

  const priorWarnings = Array.isArray(report.qualityWarnings) ? report.qualityWarnings : [];
  const reconciledWarnings = claimsDropped ? conflictWarningsForClaims(priorWarnings, claims) : priorWarnings;
  // And the stance / sentiment samples (see phraseSamplesWithoutWithheldText).
  const samples = phraseSamplesWithoutWithheldText(reconciledWarnings, leaves);
  if (samples.redacted > 0) {
    console.warn(
      `[rvg:reviewer-voice-leak] reportId=${report.id} redactedWarningSamples=${samples.redacted}`,
    );
  }
  const qualityWarnings: NonNullable<SiteRagReport['qualityWarnings']> = [
    ...samples.warnings,
    { type: 'reviewer-voice-leak', count: withheld.length, withheld },
  ];
  // The body patch has dynamic keys (the gated fields), so only it is cast;
  // the quality-warning record above is checked against the declared union.
  const patched = { ...report, ...patch } as SiteRagReport;
  return {
    ...patched,
    ...(claimsDropped ? { claims } : {}),
    ...(findings && findings.redacted > 0 ? { sanityFindings: findings.findings } : {}),
    ...(priorReview && issues && issues.redacted > 0 ? { qualityReview: { ...priorReview, issues: issues.issues } } : {}),
    qualityWarnings,
  };
}

/**
 * RVG × MEL-MR-1 — reconcile the withheld records after the section regen.
 *
 * `regenSectionsAfterMetaLeak` runs AFTER the gate and can refill a top-level
 * field the gate withheld (an empty body is below its length floor). Left as
 * is, the report would carry fresh prose AND a withheld record: held,
 * export-blocked and saying "not assessed" about text that is there. So:
 *   1. the gate runs again, so regenerated prose that itself carries reviewer
 *      voice is withheld and recorded like any other leaf (and any claim
 *      lifted from it is dropped, and any copy or quote of it is swept as
 *      above; the first pass already did all of that for every leaf it
 *      withheld, and a refilled leaf's new prose has none);
 *   2. a withheld row whose leaf now holds content (it passed step 1) is
 *      dropped; a record left with no rows is removed.
 * Pure; the identity when no withheld record exists or nothing was refilled.
 */
export function reconcileWithheldNarrativeAfterRegen(report: SiteRagReport): SiteRagReport {
  if (!report) return report;
  const gated = applyReviewerVoiceQualityGate(report);
  const warnings = Array.isArray(gated.qualityWarnings) ? gated.qualityWarnings : [];
  if (!warnings.some((warning) => warning?.type === 'reviewer-voice-leak')) return gated;

  const hasContent = (path: string): boolean => {
    const segments = parseReportLeafPath(path);
    if (!segments) return false;
    let node: unknown = gated;
    for (const segment of segments) {
      if (node === null || typeof node !== 'object') return false;
      node = (node as Record<string | number, unknown>)[segment];
    }
    return typeof node === 'string' && node.trim().length > 0;
  };

  let changed = false;
  const next: NonNullable<SiteRagReport['qualityWarnings']> = [];
  for (const warning of warnings) {
    if (warning?.type !== 'reviewer-voice-leak' || !Array.isArray(warning.withheld)) {
      next.push(warning);
      continue;
    }
    const kept = warning.withheld.filter((leaf) => !hasContent(leaf.path));
    if (kept.length === warning.withheld.length) {
      next.push(warning);
      continue;
    }
    changed = true;
    const legacy = Array.isArray(warning.sections) ? warning.sections : [];
    if (kept.length === 0 && legacy.length === 0) continue;
    next.push({ ...warning, count: kept.length + legacy.length, withheld: kept });
  }
  return changed ? { ...gated, qualityWarnings: next } : gated;
}

/** Exported for direct testing. */
export const __INTERNAL_MEL = {
  META_INSTRUCTION_PATTERNS,
} as const;

export const __INTERNAL_RVG = {
  REVIEWER_VOICE_PATTERNS,
  /** The surfaces the gate inspects (matrix tests seed every one). */
  TOP_LEVEL_STRING_FIELDS: RVG_STRING_FIELDS,
  NARRATIVE_OBJECT_FIELDS,
  ARRAY_FIELDS: RVG_ARRAY_FIELDS,
} as const;

/* ─── KFH-3 — Unresolved template-placeholder strip ──────────────── */

/**
 * Pattern matching the Title-Case bracketed template tokens that
 * should never survive to the rendered narrative. KFH-1 (the renderer)
 * and KFH-2 (the prompt-side guard) handle the duplicate-render and
 * pre-LLM halves; this is the defense-in-depth — when the model still
 * leaks a placeholder (it has been observed to invent them in the
 * exec-summary opener even after KFH-2 ships), the post-LLM strip
 * REPLACES the token with an inline `[unresolved: X]` marker so the
 * user can spot the failure rather than mistake the placeholder for a
 * legitimate citation.
 *
 * Anchored families (the live-prod leak shape PLUS the related
 * template-token shapes the prompt path could emit):
 *   - `[Project Intent X]` — exec-summary opener (Sickel v3.0 leak)
 *   - `[Site Context X]`   — Site Context dot-paths (PP4)
 *   - `[DAM Ref X]`        — DAM cross-ref shape
 *   - `[Doc Ref X]`        — uploaded-doc filename shape
 *   - `[SED Slot X]`       — SED variable slot shape
 *   - `[Workload Profile]` and `[Workload X]`
 *   - `[Capacity X]`       — power-requirement shape
 *
 * Citation refs are `[Ref N]` and the reliability tags / verifier
 * markers (`[verified …]`, `[inferred …]`, `[unverified …]`,
 * `[ADAMS: ML…]`) are distinct by shape — those are case-sensitive
 * lowercase tags or the all-caps ADAMS shape, so the anchored
 * Title-Case-prefix list above cannot overlap.
 *
 * `g` flag — global so we count every occurrence; the regex is NOT
 * case-insensitive: matching is intentionally case-sensitive on the
 * Title-Case prefix because `[verified project intent grid]` is a
 * legitimate reliability tag (lowercase "project intent" appears
 * inside an explicit `verified` prefix and is allowed).
 */
export const PLACEHOLDER_TOKEN_REGEX =
  /\[(Project Intent|Site Context|DAM Ref|Doc Ref|SED Slot|Workload Profile|Workload|Capacity)(?:\s+[^\]]+)?\]/g;

/**
 * Replace every unresolved placeholder in `body` with `[unresolved: X]`
 * (where X is the bracketed token content minus the outer brackets) so
 * the user sees the failure inline instead of a legitimate-looking
 * citation. Returns `{ next, matches }` where `matches` is the list of
 * raw placeholder strings found (used to drive the qualityWarnings
 * stamp).
 *
 * Pure / side-effect free.
 */
export function stripUnresolvedPlaceholdersFromText(
  body: string,
): { next: string; matches: string[] } {
  if (!body) return { next: body, matches: [] };
  const matches: string[] = [];
  const re = new RegExp(PLACEHOLDER_TOKEN_REGEX.source, PLACEHOLDER_TOKEN_REGEX.flags);
  const next = body.replace(re, (m) => {
    matches.push(m);
    // Slice off the outer [ ] and re-wrap so the user sees the
    // failure inline. Idempotent: if `m` already starts with
    // `[unresolved:` it can't (the regex doesn't match that prefix),
    // so we don't need an idempotency guard.
    return `[unresolved: ${m.slice(1, -1)}]`;
  });
  if (matches.length === 0) return { next: body, matches: [] };
  return { next, matches };
}

/**
 * Walk a narrative value and strip placeholder tokens from every
 * string leaf. Mirrors `stripFromValue` (RNH-5) and `stripMetaFromValue`
 * (MEL) so the strip surface stays in sync with the detection surface.
 */
function stripPlaceholdersFromValue(
  value: unknown,
  depth = 0,
): { next: unknown; matches: string[] } {
  if (value === null || value === undefined) return { next: value, matches: [] };
  if (typeof value === 'string') return stripUnresolvedPlaceholdersFromText(value);
  if (Array.isArray(value)) {
    if (depth >= 3) return { next: value, matches: [] };
    const allMatches: string[] = [];
    let mutated = false;
    const nextArr = value.map(v => {
      const r = stripPlaceholdersFromValue(v, depth + 1);
      if (r.matches.length > 0) {
        mutated = true;
        allMatches.push(...r.matches);
      }
      return r.next;
    });
    return mutated ? { next: nextArr, matches: allMatches } : { next: value, matches: [] };
  }
  if (typeof value === 'object') {
    if (depth >= 3) return { next: value, matches: [] };
    const allMatches: string[] = [];
    let mutated = false;
    const nextObj: Record<string, unknown> = { ...(value as Record<string, unknown>) };
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const r = stripPlaceholdersFromValue(v, depth + 1);
      if (r.matches.length > 0) {
        mutated = true;
        allMatches.push(...r.matches);
        nextObj[k] = r.next;
      }
    }
    return mutated ? { next: nextObj, matches: allMatches } : { next: value, matches: [] };
  }
  return { next: value, matches: [] };
}

/**
 * KFH-3 — strip unresolved template-placeholder tokens from every
 * narrative surface AND stamp `report.qualityWarnings += [{
 * type: 'unresolved-template-placeholder', count, samples }]` so the
 * renderer can flag affected sections for manual review even after the
 * strip.
 *
 * Pure: returns identity when no placeholder tokens were found
 * anywhere; a shallow-cloned report with mutated subtrees + a new
 * `qualityWarnings` array when at least one token was replaced.
 *
 * MUST run AFTER `applyClaimsEmission`, `stripVerifierTags`,
 * `flagAssertiveNuclearActions`, and `stripMetaInstructions` (those
 * passes read inline tokens and would be confused by the
 * `[unresolved: ...]` rewrites we produce). The intended pipeline
 * order:
 *   generateNarrative → applyClaimsEmission → stripVerifierTags →
 *   flagAssertiveNuclearActions → stripMetaInstructions →
 *   stripUnresolvedPlaceholders → persist.
 *
 * Also walks the same key surfaces RNH/MEL walk (TOP_LEVEL_STRING_FIELDS
 * + NARRATIVE_OBJECT_FIELDS + developmentPathway/recommendations/
 * appendix arrays + the keyFindings-adjacent shapes).
 */
export function stripUnresolvedPlaceholders(report: SiteRagReport): SiteRagReport {
  if (!report) return report;
  const allMatches: string[] = [];
  const patch: Record<string, unknown> = {};

  for (const key of TOP_LEVEL_STRING_FIELDS) {
    const v = (report as unknown as Record<string, unknown>)[key];
    if (typeof v !== 'string') continue;
    const r = stripUnresolvedPlaceholdersFromText(v);
    if (r.matches.length > 0) {
      allMatches.push(...r.matches);
      patch[key] = r.next;
    }
  }

  for (const key of NARRATIVE_OBJECT_FIELDS) {
    const v = (report as unknown as Record<string, unknown>)[key];
    const r = stripPlaceholdersFromValue(v);
    if (r.matches.length > 0) {
      allMatches.push(...r.matches);
      patch[key] = r.next;
    }
  }

  for (const key of ['developmentPathway', 'recommendations', 'appendix'] as const) {
    const v = (report as unknown as Record<string, unknown>)[key];
    if (!Array.isArray(v)) continue;
    const r = stripPlaceholdersFromValue(v);
    if (r.matches.length > 0) {
      allMatches.push(...r.matches);
      patch[key] = r.next;
    }
  }

  // Walk the structuredFindings/keyFindings/claims arrays — the
  // live-prod leak hit Key Findings, so these surfaces MUST be covered
  // (not all of them appear in the RNH/MEL walk lists).
  for (const key of ['projectIntentOverview', 'structuredFindings', 'contextualKeyFindings', 'keyFindings', 'claims'] as const) {
    const v = (report as unknown as Record<string, unknown>)[key];
    if (!Array.isArray(v)) continue;
    const r = stripPlaceholdersFromValue(v);
    if (r.matches.length > 0) {
      allMatches.push(...r.matches);
      patch[key] = r.next;
    }
  }

  if (allMatches.length === 0) return report;

  console.info(
    `[kfh:unresolved-template-placeholder] reportId=${report.id} count=${allMatches.length}`,
  );

  const existingWarnings = Array.isArray(report.qualityWarnings) ? report.qualityWarnings : [];
  // Roll up samples — distinct token strings, capped at 5 so the
  // warnings blob stays small in Firestore.
  const distinctSamples = Array.from(new Set(allMatches)).slice(0, 5);
  const newWarning = {
    type: 'unresolved-template-placeholder' as const,
    count: allMatches.length,
    samples: distinctSamples,
  };

  return {
    ...report,
    ...patch,
    qualityWarnings: [...existingWarnings, newWarning],
  } as SiteRagReport;
}

/**
 * SEC — flag when consolidated coverage manifest field values do not
 * appear in the generated narrative (soft informational warning).
 */
export function flagCoverageManifestDrift(report: SiteRagReport): SiteRagReport {
  const manifest = report.coverageManifestAtGen;
  if (!manifest?.entries?.length) return report;

  const narrativeParts: string[] = [];
  const walk = (v: unknown): void => {
    if (typeof v === 'string' && v.trim().length > 0) narrativeParts.push(v);
    else if (Array.isArray(v)) v.forEach(walk);
    else if (v && typeof v === 'object') Object.values(v as Record<string, unknown>).forEach(walk);
  };
  walk(report);
  const narrativeText = narrativeParts.join('\n');

  const drifts = findManifestDrift(manifest, narrativeText);
  if (drifts.length === 0) return report;

  const existingWarnings = Array.isArray(report.qualityWarnings) ? report.qualityWarnings : [];
  return {
    ...report,
    qualityWarnings: [
      ...existingWarnings,
      {
        type: 'sec-coverage-manifest-drift' as const,
        count: drifts.length,
        samples: drifts.slice(0, 5),
      },
    ],
  };
}

/** Exported for direct testing. */
export const __INTERNAL_KFH = {
  PLACEHOLDER_TOKEN_REGEX,
} as const;
